/**
 * Die Auswahl auf der Gast-Angebotsseite: Menükarten, „Auf Wunsch dazu“, Gäste-Zähler bei Wahlgängen
 * („oder“) und der Kasten „Ihre Auswahl“ (rechts mitlaufend, auf dem Handy als Blatt).
 *
 * Nichts hier ist an einen Betrieb, ein Gericht, eine Zahl oder eine Anzahl von Alternativen gebunden:
 * alles kommt aus dem Angebot (Positionen, Gänge, Wahlgruppen, Zubuchungen und ihre Art, „und“-
 * Verbindungen, Preise, USt, Anzahlung). Die Texte sind Vorlagen mit Platzhaltern.
 *
 * Beträge werden hier nur ANGEZEIGT. Die Vorschau addiert/subtrahiert dieselben beim Versand
 * eingefrorenen Ganzzahlen wie der Server (`angebot-auswahl-modell.ts`); verbindlich ist, was der Server
 * bei der Annahme rechnet (sql/752). Die Anzahlung kommt fertig vom Server.
 *
 * Farben: die Markenfarbe des Betriebs (tenant_settings.brand_primary_color) trägt Knöpfe, Rahmen der
 * gewählten Karte, Schalter und Zähler; Fallback ist der bisherige Akzent. Kein helles Rot als Fläche.
 */
import { Fragment, createContext, useContext, useEffect, useLayoutEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { money } from "./format";
import { Icon, Radio } from "./components";
import { DIETARY } from "./lmiv";
import { A_LA_CARTE_GAST } from "../lib/a-la-carte";
import type { PublicOffer, PublicOfferItem } from "./types";
import {
  alternativenAus,
  gaengeAus,
  menge,
  rechne,
  schalterAus,
  gerichtTeile,
  speisekarteZeilen,
  wiederholtTitel,
  paketBestandteile,
  alternativenAus as alternativenBloecke,
  gaengeAus as gaengeBloecke,
  ustAufteilung,
  aufzaehlungAus,
  wahlStand,
  alternativeAufpreis,
  wahlgaengeAus,
  wahlgangStand,
  wahlSoll,
  menuewahlenAus,
  menuewahlPositionen,
  menuewahlLage,
  menuewahlSperrt,
  menuewahlZeile,
  menuewahlStandardFrist,
  zeilenbetrag,
  zubuchungenAus,
  nurZubuchungen,
  type AngebotOption,
  type Mengen,
  type Rechnung,
  type Wahl,
  type Wahlgang,
  type Menuewahl,
  type KartenZeile,
  type Zubuch,
  type ZubuchSchalter,
} from "./angebot-auswahl-modell";

export const AUSWAHL_FALLBACK_AKZENT = "#c2410c";

/* ───────────── Texte (Vorlagen) ───────────── */

export const AT = {
  de: {
    waehlen: "Dieses Menü wählen",
    gewaehlt: "Ausgewählt",
    ihreWahl: "Ihre Wahl",
    menue: "Menü",
    proPerson: "pro Person",
    gaeste: (n: number) => (n === 1 ? "1 Gast" : `${n} Gäste`),
    gaesteUndBetrag: (n: number, betrag: string) => `${n === 1 ? "1 Gast" : `${n} Gäste`} · ${betrag}`,
    ersetztEine: (n: number, name: string) => `${n === 1 ? "1 Gast" : `${n} Gäste`}: ${name}, siehe unten`,
    ersetztArt: (n: number, art: string) => `${n === 1 ? "1 Gast" : `${n} Gäste`} ${art}, siehe unten`,
    ersetztMehrere: (n: number) => `${n === 1 ? "1 Gast" : `${n} Gäste`} statt Hauptmenü, siehe unten`,
    gaesteMal: (n: number, betrag: string) => `${n === 1 ? "1 Gast" : `${n} Gäste`} × ${betrag}`,
    gaengeKurz: (n: number) => `${n} Gänge`,
    aperitifInkl: "Aperitif inklusive",
    getraenkeTitel: "Getränke",
    getraenkeZubuchbar: "Nicht enthalten. Als Getränkepauschale für alle Gäste zubuchbar, siehe unten.",
    getraenkeEinleitung: "Passend zum Menü, für die ganze Gesellschaft. Enthalten sind:",
    stattHauptmenue: "statt dem Hauptmenü",
    ersetztErklaerung: (wer: string, menue: string, rest: number) => ({ vor: `Diese Gäste erhalten ${wer} `, fett: `statt ${menue}`, nach: `. Hauptmenü: `, zahl: String(rest), ende: rest === 1 ? " Gast." : " Gäste." }),
    ersetztErklaerungLeer: (menue: string, alle: number) => ({ vor: "Wer dieses Menü möchte, erhält es ", fett: `statt ${menue}`, nach: `. Höchstens ${alle} Gäste.` }),
    dasMenue: (adjektiv: string) => `das ${adjektiv}e Menü`,
    nachKarte: "Nach Karte",
    wunschTitel: "Auf Wunsch dazu",
    wunschHinweis: "Nichts ist vorausgewählt. Sie zahlen nur, was Sie hinzufügen.",
    wunschGesperrt: "Wählen Sie zuerst dieses Menü, dann können Sie hinzufügen.",
    wunschNurAnsicht: "In dieser Ansicht lässt sich nichts hinzufügen.",
    fuerAlle: (n: number) => `für alle ${n} Gäste`,
    jeStueck: "je Einheit",
    statt: (menue: string, rest: number, alle: number) => `statt „${menue}“ · ${rest} von ${alle} Gästen behalten das Hauptmenü`,
    stattOhneTitel: (rest: number, alle: number) => `statt Hauptmenü · ${rest} von ${alle} Gästen behalten das Hauptmenü`,
    mindestumsatzInkl: (betrag: string) => `Mindestumsatz ${betrag} inkl. Menü`,
    mindestumsatzIst: (ist: string, ergaenzt: string) => `Ihre Auswahl: ${ist} – wir ergänzen ${ergaenzt}`,
    mindestumsatzErreicht: "Mindestumsatz erreicht",
    aufstockung: (name: string) => `Aufstockung ${name}`,
    zusatzOhnePreis: "Auf Wunsch, nicht im Preis enthalten – bitte sprechen Sie uns an.",
    zuschlagHinweis: "zusätzlich zur Pauschale",
    schalterAn: (name: string) => `${name}: hinzufügen`,
    aufpreisPP: (x: string) => `+ ${x} p. P.`,
    wahlKopf: (n: number) => `Wie viele Gäste nehmen was? · ${n === 1 ? "1 Gast" : `${n} Gäste`}`,
    wahlOffen: (n: number) => `noch ${n === 1 ? "1 Gast" : `${n} Gäste`} offen`,
    wahlFertig: (n: number) => `alle ${n === 1 ? "1 Gast" : `${n} Gäste`} zugeordnet`,
    wahlZuViel: (n: number) => `${n === 1 ? "1 Gast" : `${n} Gäste`} zu viel`,
    bitteWaehlen: "Bitte wählen",
    menueKopf: (n: number) => `Ihre Menüauswahl · ${n === 1 ? "1 Gast" : `${n} Gäste`}`,
    menueKopfGebucht: (n: number) => `Ihre Menüauswahl · ${n === 1 ? "1 Gast" : `${n} Gäste`}`,
    menueOffen: (n: number) => `Noch ${n === 1 ? "1 Gast" : `${n} Gäste`} zuordnen`,
    menueFertig: (n: number) => `Alle ${n === 1 ? "1 Gast" : `${n} Gäste`} zugeordnet`,
    menueZuViel: (n: number) => `${n === 1 ? "1 Gast" : `${n} Gäste`} zu viel`,
    menueNichtFestgelegt: "Noch nicht festgelegt",
    menueRegel: (n: number) => `Erst wenn alle ${n === 1 ? "1 Gast" : `${n} Gäste`} einem Menü zugeordnet sind, können Sie das Angebot annehmen.`,
    menueSpaeterBis: (datum: string) => `Sie können die Menüs bis ${datum} festlegen. Wir erinnern Sie rechtzeitig.`,
    menueSpaeterOhneDatum: "Sie können die Menüs auch nach der Buchung festlegen. Wir schicken Ihnen dann den Link.",
    menueAufpreis: (x: string) => `+ ${x} pro Gast`,
    menueOhneAufpreis: "ohne Aufpreis",
    menueGaeste: "Gäste",
    menueArt: { vegan: "vegan", vegetarisch: "vegetarisch", fisch_fleisch: "Fisch/Fleisch" } as Record<string, string>,
    hinweisMenue: (n: number) => `Bitte noch ${n === 1 ? "1 Gast" : `${n} Gäste`} auf die Menüs verteilen`,
    hinweisMenueZuViel: (n: number) => `Bei den Menüs sind ${n === 1 ? "1 Gast" : `${n} Gäste`} zu viel zugeordnet`,
    menueBestaetigen: "Menüauswahl bestätigen",
    menueSpeichert: "Wird gespeichert …",
    menueDanke: "Vielen Dank, Ihre Menüauswahl ist gespeichert.",
    menueMehrbetrag: (x: string) => `Mehrbetrag durch Ihre Auswahl: ${x}.`,
    menueMinderbetrag: (x: string) => `Minderbetrag durch Ihre Auswahl: ${x}. Er wird in der Schlussrechnung verrechnet.`,
    menueFrist: (datum: string) => `Bitte bis ${datum} festlegen`,
    menueSpeichertFehler: "Die Menüauswahl konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.",
    menueFristAbgelaufen: "Die Frist für die Menüauswahl ist abgelaufen. Bitte wenden Sie sich direkt an das Restaurant.",
    menueGebuchtTitel: "Bitte legen Sie Ihre Menüs fest",
    menueGebuchtText: (n: number, datum: string | null) => `Verteilen Sie Ihre ${n === 1 ? "1 Gast" : `${n} Gäste`} auf die Menüs${datum ? ` – bis ${datum}` : ""}. Der Preis des Pakets bleibt gleich.`,
    menueGebuchtTextAufpreis: (n: number, datum: string | null) => `Verteilen Sie Ihre ${n === 1 ? "1 Gast" : `${n} Gäste`} auf die Menüs${datum ? ` – bis ${datum}` : ""}. Menüs mit Aufpreis ändern den Betrag.`,
    menueGebuchtDanke: "Ihre Menüauswahl",
    oder: "oder",
    weniger: "Weniger",
    mehr: "Mehr",
    anzahlFuer: (name: string) => `Anzahl Gäste: ${name}`,
    anzahlZubuch: (name: string) => `Anzahl: ${name}`,
    kastenTitel: "Ihre Auswahl",
    kastenLeer: "Wählen Sie ein Menü. Danach erscheinen hier Ihre Auswahl und die Summe.",
    gesamt: "Summe brutto",
    inklMwst: "inkl. MwSt.",
    davonUst: (teile: string[]) => `davon ${teile.join(" · ")}`,
    ustTeil: (satz: string, betrag: string) => `${satz} %: ${betrag}`,
    anzahlung: "Anzahlung bei Annahme",
    annahmeVerbindlich: "Die Annahme ist verbindlich. Sie erhalten die Bestätigung per E-Mail.",
    angenommenBadge: "Angebot angenommen",
    angenommenGebucht: "Ihre Auswahl ist verbindlich gebucht.",
    angenommenHinweis: "Änderungen bitte direkt mit dem Restaurant abstimmen.",
    zubuchungenTitel: "Zubuchungen",
    verbindlichGebucht: "Verbindlich gebucht",
    angenommenKurz: "Angenommen",
    gewaehltKurz: "Gewählt",
    nichtGebucht: "Nicht gebucht",
    wahlKopfGebucht: (n: number) => `Ihre Wahl · ${n === 1 ? "1 Gast" : `${n} Gäste`}`,
    einzelHinweisGebucht: "Ihre Wahl, gilt für alle Gäste",
    vollzahlung: "Zahlung",
    anzahlungSpaeter: "Die Anzahlung wird nach Ihrer Auswahl berechnet.",
    /** „(30 %)“ - der Satz kommt in Basispunkten vom Server, hier wird nur formatiert. */
    anzahlungProzent: (bp: number) => `(${(bp / 100).toLocaleString("de-DE", { maximumFractionDigits: 2 })} %)`,
    annehmen: "Angebot annehmen",
    hinweisWahl: (gang: string, n: number) => `Bitte beim ${gang} noch ${n === 1 ? "1 Gast" : `${n} Gäste`} zuordnen`,
    hinweisZuViel: (gang: string, n: number) => `Beim ${gang} sind ${n === 1 ? "1 Gast" : `${n} Gäste`} zu viel zugeordnet`,
    wahlgangName: "Wahlgang",
    hinweisUeberbucht: "Die Auswahl übersteigt die Gästezahl.",
    hinweisVorschau: "In der Vorschau deaktiviert",
    meineAuswahl: "Meine Auswahl",
    schliessen: "Schließen",
    speichertFehler: "Die Zuordnung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.",
    gerichteStueck: (n: number, name: string) => `${n} × ${name}`,
    einzelHinweis: "Bitte eines wählen – gilt für alle Gäste",
    einzelWaehlen: "Wählen",
    erstMenue: "Erst Menü wählen",
    fuerAlleSchalter: (n: number) => `Für alle ${n} Gäste`,
    /** sql/763: Frage über dem Gäste-Zähler einer per_person-Zubuchung (anderes Menü, Getränke …). */
    fuerWieviele: "Für wie viele Gäste?",
    /** Variante ohne festen Preis (nur Zubuchungen, Karte 0,00 €): statt „0,00 € pro Person“. */
    nurWahlPreis: "Bitte unten wählen, was Sie buchen möchten",
    hinweisNichtsGewaehlt: "Bitte unten wählen, was Sie buchen möchten.",
    hinzufuegen: "Hinzufügen",
    zusaetzlich: "Zusätzlich",
    pauschal: "pauschal",
    zaehltNichtMu: "zählt nicht zum Mindestumsatz",
    gebucht: "Gäste",
  },
  en: {
    waehlen: "Choose this menu",
    gewaehlt: "Selected",
    ihreWahl: "Your choice",
    menue: "Menu",
    proPerson: "per person",
    gaeste: (n: number) => (n === 1 ? "1 guest" : `${n} guests`),
    gaesteUndBetrag: (n: number, betrag: string) => `${n === 1 ? "1 guest" : `${n} guests`} · ${betrag}`,
    ersetztEine: (n: number, name: string) => `${n === 1 ? "1 guest" : `${n} guests`}: ${name}, see below`,
    ersetztArt: (n: number, art: string) => `${n === 1 ? "1 guest" : `${n} guests`} ${art}, see below`,
    ersetztMehrere: (n: number) => `${n === 1 ? "1 guest" : `${n} guests`} instead of the main menu, see below`,
    gaesteMal: (n: number, betrag: string) => `${n === 1 ? "1 guest" : `${n} guests`} × ${betrag}`,
    gaengeKurz: (n: number) => `${n} courses`,
    aperitifInkl: "Aperitif included",
    getraenkeTitel: "Drinks",
    getraenkeZubuchbar: "Not included. Can be added as a drinks package for all guests, see below.",
    getraenkeEinleitung: "To go with the menu, for the whole party. Included:",
    stattHauptmenue: "instead of the main menu",
    ersetztErklaerung: (wer: string, menue: string, rest: number) => ({ vor: `These guests receive ${wer} `, fett: `instead of ${menue}`, nach: `. Main menu: `, zahl: String(rest), ende: rest === 1 ? " guest." : " guests." }),
    ersetztErklaerungLeer: (menue: string, alle: number) => ({ vor: "Guests who would like this menu receive it ", fett: `instead of ${menue}`, nach: `. At most ${alle} guests.` }),
    dasMenue: (adjektiv: string) => `the ${adjektiv} menu`,
    nachKarte: "À la carte",
    wunschTitel: "Add on request",
    wunschHinweis: "Nothing is preselected. You only pay for what you add.",
    wunschGesperrt: "Choose this menu first, then you can add items.",
    wunschNurAnsicht: "Nothing can be added in this view.",
    fuerAlle: (n: number) => `for all ${n} guests`,
    jeStueck: "per unit",
    statt: (menue: string, rest: number, alle: number) => `instead of “${menue}” · ${rest} of ${alle} guests keep the main menu`,
    stattOhneTitel: (rest: number, alle: number) => `instead of the main menu · ${rest} of ${alle} guests keep the main menu`,
    mindestumsatzInkl: (betrag: string) => `Minimum spend ${betrag} incl. menu`,
    mindestumsatzIst: (ist: string, ergaenzt: string) => `Your selection: ${ist} – we add ${ergaenzt}`,
    mindestumsatzErreicht: "Minimum spend reached",
    aufstockung: (name: string) => `Top-up ${name}`,
    zusatzOhnePreis: "On request, not included in the price – please ask us.",
    zuschlagHinweis: "in addition to the flat rate",
    schalterAn: (name: string) => `${name}: add`,
    aufpreisPP: (x: string) => `+ ${x} p. p.`,
    wahlKopf: (n: number) => `How many guests take what? · ${n === 1 ? "1 guest" : `${n} guests`}`,
    wahlOffen: (n: number) => `${n === 1 ? "1 guest" : `${n} guests`} still open`,
    wahlFertig: (n: number) => `all ${n === 1 ? "1 guest" : `${n} guests`} assigned`,
    wahlZuViel: (n: number) => `${n === 1 ? "1 guest" : `${n} guests`} too many`,
    bitteWaehlen: "Please choose",
    menueKopf: (n: number) => `Your menu choice · ${n === 1 ? "1 guest" : `${n} guests`}`,
    menueKopfGebucht: (n: number) => `Your menu choice · ${n === 1 ? "1 guest" : `${n} guests`}`,
    menueOffen: (n: number) => `${n === 1 ? "1 guest" : `${n} guests`} still to assign`,
    menueFertig: (n: number) => `All ${n === 1 ? "1 guest" : `${n} guests`} assigned`,
    menueZuViel: (n: number) => `${n === 1 ? "1 guest" : `${n} guests`} too many`,
    menueNichtFestgelegt: "Not chosen yet",
    menueRegel: (n: number) => `You can accept the offer once all ${n === 1 ? "1 guest" : `${n} guests`} are assigned to a menu.`,
    menueSpaeterBis: (datum: string) => `You can choose the menus until ${datum}. We will remind you in good time.`,
    menueSpaeterOhneDatum: "You can also choose the menus after booking. We will then send you the link.",
    menueAufpreis: (x: string) => `+ ${x} per guest`,
    menueOhneAufpreis: "no surcharge",
    menueGaeste: "Guests",
    menueArt: { vegan: "vegan", vegetarisch: "vegetarian", fisch_fleisch: "fish/meat" } as Record<string, string>,
    hinweisMenue: (n: number) => `Please assign ${n === 1 ? "1 more guest" : `${n} more guests`} to the menus`,
    hinweisMenueZuViel: (n: number) => `Menus: ${n === 1 ? "1 guest" : `${n} guests`} too many assigned`,
    menueBestaetigen: "Confirm menu choice",
    menueSpeichert: "Saving …",
    menueDanke: "Thank you, your menu choice has been saved.",
    menueMehrbetrag: (x: string) => `Additional amount for your choice: ${x}.`,
    menueMinderbetrag: (x: string) => `Reduction for your choice: ${x}. It will be offset in the final invoice.`,
    menueFrist: (datum: string) => `Please choose by ${datum}`,
    menueSpeichertFehler: "Your menu choice could not be saved. Please try again.",
    menueFristAbgelaufen: "The deadline for the menu choice has passed. Please contact the restaurant directly.",
    menueGebuchtTitel: "Please choose your menus",
    menueGebuchtText: (n: number, datum: string | null) => `Assign your ${n === 1 ? "1 guest" : `${n} guests`} to the menus${datum ? ` – by ${datum}` : ""}. The package price stays the same.`,
    menueGebuchtTextAufpreis: (n: number, datum: string | null) => `Assign your ${n === 1 ? "1 guest" : `${n} guests`} to the menus${datum ? ` – by ${datum}` : ""}. Menus with a surcharge change the amount.`,
    menueGebuchtDanke: "Your menu choice",
    oder: "or",
    weniger: "Fewer",
    mehr: "More",
    anzahlFuer: (name: string) => `Number of guests: ${name}`,
    anzahlZubuch: (name: string) => `Quantity: ${name}`,
    kastenTitel: "Your selection",
    kastenLeer: "Choose a menu. Your selection and the total will then appear here.",
    gesamt: "Total (gross)",
    inklMwst: "incl. VAT",
    davonUst: (teile: string[]) => `of which ${teile.join(" · ")}`,
    ustTeil: (satz: string, betrag: string) => `${satz} %: ${betrag}`,
    anzahlung: "Deposit on acceptance",
    annahmeVerbindlich: "Acceptance is binding. You will receive a confirmation by email.",
    angenommenBadge: "Offer accepted",
    angenommenGebucht: "Your selection is booked and binding.",
    angenommenHinweis: "Please arrange any changes directly with the restaurant.",
    zubuchungenTitel: "Add-ons",
    verbindlichGebucht: "Booked and binding",
    angenommenKurz: "Accepted",
    gewaehltKurz: "Selected",
    nichtGebucht: "Not booked",
    wahlKopfGebucht: (n: number) => `Your choice · ${n === 1 ? "1 guest" : `${n} guests`}`,
    einzelHinweisGebucht: "Your choice, applies to all guests",
    vollzahlung: "Payment",
    anzahlungSpaeter: "The deposit is calculated after your selection.",
    anzahlungProzent: (bp: number) => `(${(bp / 100).toLocaleString("en-GB", { maximumFractionDigits: 2 })} %)`,
    annehmen: "Accept offer",
    hinweisWahl: (gang: string, n: number) => `Please assign ${n === 1 ? "1 more guest" : `${n} more guests`} for ${gang}`,
    hinweisZuViel: (gang: string, n: number) => `${gang}: ${n === 1 ? "1 guest" : `${n} guests`} too many assigned`,
    wahlgangName: "this course",
    hinweisUeberbucht: "The selection exceeds the number of guests.",
    hinweisVorschau: "Disabled in the preview",
    meineAuswahl: "My selection",
    schliessen: "Close",
    speichertFehler: "The assignment could not be saved. Please try again.",
    gerichteStueck: (n: number, name: string) => `${n} × ${name}`,
    einzelHinweis: "Please choose one – applies to all guests",
    einzelWaehlen: "Choose",
    erstMenue: "Choose a menu first",
    fuerAlleSchalter: (n: number) => `For all ${n} guests`,
    fuerWieviele: "For how many guests?",
    nurWahlPreis: "Please choose below what you would like to book",
    hinweisNichtsGewaehlt: "Please choose below what you would like to book.",
    hinzufuegen: "Add",
    zusaetzlich: "In addition",
    pauschal: "flat rate",
    zaehltNichtMu: "does not count towards the minimum spend",
    gebucht: "guests",
  },
} as const;
export type AuswahlTexte = (typeof AT)["de"] | (typeof AT)["en"];

/* ───────────── Farben ───────────── */

function hexZuRgb(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function leuchtdichte([r, g, b]: [number, number, number]): number {
  const f = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
/** Kontrastverhältnis zweier Farben (WCAG). */
export function kontrast(a: string, b: string): number {
  const ra = hexZuRgb(a);
  const rb = hexZuRgb(b);
  if (!ra || !rb) return 1;
  const la = leuchtdichte(ra);
  const lb = leuchtdichte(rb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
/** Akzent und die Schriftfarbe darauf: Weiß, solange es 4,5:1 hält — sonst das dunkle Textbraun. */
export function akzentFarben(markenfarbe: string | null | undefined): { akzent: string; auf: string } {
  const akzent = markenfarbe && hexZuRgb(markenfarbe) ? markenfarbe : AUSWAHL_FALLBACK_AKZENT;
  const auf = kontrast(akzent, "#ffffff") >= 4.5 ? "#ffffff" : "#262119";
  return { akzent, auf };
}

/* ───────────── Stil ───────────── */

const STIL_CSS = `
.ga{--ga-akzent:#c2410c;--ga-auf:#fff;--ga-karte:#fffdf8;--ga-text:#262119;--ga-sek:#6f675a;--ga-linie:#e3dbcb;--ga-rand:#c9bfac;--ga-seite:#f4efe6;--ga-tief:#fbf6ec;--ga-hoch:#efe8da;--ga-serif:"Newsreader",Georgia,serif;--ga-sans:"Manrope",system-ui,sans-serif;color:var(--ga-text);font-family:var(--ga-sans);font-size:15px;line-height:1.5;margin-inline:calc((100% - max(100%, min(1440px, 100vw - 64px))) / 2)}
.ga *{box-sizing:border-box}
.ga :where(p){margin:0}
.ga-layout{display:grid;grid-template-columns:minmax(0,1fr);gap:24px;align-items:start}
@media (min-width:1180px){.ga-layout{grid-template-columns:minmax(0,1fr) 330px}}
.ga-nur-ansicht{margin:0 0 14px;font-size:13.5px;color:var(--ga-sek)}
.ga-karten{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,380px),1fr));column-gap:24px;row-gap:0;align-items:start}
.ga-karten[data-n="1"]{grid-template-columns:minmax(0,640px)}
.ga-karte{display:flex;flex-direction:column;background:var(--ga-karte);border:1px solid var(--ga-linie);border-radius:18px;overflow:hidden;margin-bottom:24px;position:relative;transition:border-color .15s,box-shadow .15s}
.ga-karte[data-gewaehlt="true"]{border:2px solid var(--ga-akzent);box-shadow:0 12px 32px rgba(38,33,25,.10)}
@supports (grid-template-rows:subgrid){
  .ga-karte{display:grid;grid-row:span 6;grid-template-rows:subgrid;row-gap:0}
}
.ga-foto{position:relative;height:170px;overflow:hidden;background:var(--ga-seite)}
.ga-foto img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:bottom}
.ga-marke{position:absolute;right:12px;top:12px;display:inline-flex;align-items:center;gap:6px;background:var(--ga-akzent);color:var(--ga-auf);border-radius:999px;padding:6px 12px 6px 10px;font-size:11.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase}
.ga-empfehlung{position:absolute;left:12px;top:12px;background:var(--ga-karte);color:var(--ga-text);border-radius:999px;padding:6px 12px;font-size:11.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;display:flex;gap:4px;align-items:center}
.ga-teil{padding-left:24px;padding-right:24px}
.ga-kopf{padding-top:22px;padding-bottom:4px}
.ga-unter{margin:0 0 6px;color:var(--ga-sek);font-size:12px;letter-spacing:.08em;text-transform:uppercase;font-weight:600}
.ga-titel{font-family:var(--ga-serif);font-size:25px;line-height:1.2;font-weight:500;margin:0;letter-spacing:-.005em;text-wrap:balance}
.ga-einordnung{margin:8px 0 0;font-size:13.5px;line-height:1.55;color:var(--ga-sek);max-width:62ch}
.ga-preis{padding-top:14px;display:flex;align-items:baseline;flex-wrap:wrap;gap:2px 8px;font-family:var(--ga-serif);font-size:34px;font-weight:500;line-height:1;font-variant-numeric:tabular-nums}
.ga-preis small{font-family:var(--ga-sans);font-size:13px;font-weight:400;color:var(--ga-sek);line-height:1.4}
.ga-gaeste{padding-top:8px;padding-bottom:16px;font-size:13px;color:var(--ga-sek);font-variant-numeric:tabular-nums}
.ga-gaeste p{min-height:19.5px}
.ga-gaeste b{color:var(--ga-text);font-weight:700}
.ga-knopf-zelle{padding:0 24px 22px;border-bottom:1px solid var(--ga-linie)}
.ga-knopf{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;height:48px;border-radius:12px;border:1.5px solid var(--ga-akzent);background:var(--ga-akzent);color:var(--ga-auf);font-family:var(--ga-sans);font-size:14.5px;font-weight:700;cursor:pointer;transition:filter .15s}
.ga-knopf:hover{filter:brightness(.92)}
.ga-knopf[aria-pressed="true"]{background:var(--ga-tief);color:var(--ga-text);border-color:var(--ga-rand);filter:none}
.ga-knopf[aria-pressed="true"] .ga-haken{color:var(--ga-text)}
.ga-knopf:focus-visible,.ga button:focus-visible,.ga input:focus-visible{outline:3px solid var(--ga-akzent);outline-offset:2px}
.ga-koerper{container-type:inline-size;padding:0 0 24px;display:flex;flex-direction:column;min-width:0}
.ga-rubrik{min-width:0}
.ga-sektion-titel{font-family:var(--ga-serif);font-size:20px;font-weight:500;margin:0;padding:22px 24px 0}
.ga-gang{padding:18px 24px 0;min-width:0}
.ga-gang-titel{display:flex;align-items:center;gap:12px;margin:0 0 8px;font-family:var(--ga-sans);font-size:10.5px;letter-spacing:.2em;font-weight:700;color:var(--ga-sek);text-transform:uppercase}
.ga-gang-titel::after{content:"";flex:1;height:1px;background:var(--ga-linie)}
.ga-gericht{font-family:var(--ga-serif);font-size:19px;line-height:1.3;font-weight:500;margin:0;text-wrap:balance;overflow-wrap:normal;word-break:normal;hyphens:manual}
.ga-gericht-zusatz{font-family:var(--ga-sans);font-size:12.5px;font-weight:400;color:var(--ga-sek)}
.ga-gericht-art{font-size:10.5px;letter-spacing:.16em;text-transform:uppercase;font-weight:700;color:var(--ga-sek);margin:0 0 3px}
.ga-gericht-text{font-size:13.5px;line-height:1.55;color:var(--ga-sek);margin:3px 0 0;overflow-wrap:normal;hyphens:manual}
.ga-gericht-aufpreis{font-size:13px;font-weight:700;color:var(--ga-text);margin:6px 0 0;font-variant-numeric:tabular-nums}
.ga-liste{list-style:none;margin:0;padding:0}
.ga-zeile+.ga-zeile{margin-top:12px}
.ga-oder{display:flex;align-items:center;gap:10px;margin:0 0 10px;color:var(--ga-sek);font-family:var(--ga-serif);font-style:italic;font-size:14px}
.ga-oder::before,.ga-oder::after{content:"";flex:1;height:1px;background:var(--ga-linie)}
.ga-notiz{font-size:13px;font-style:italic;color:var(--ga-sek);margin:3px 0 0}
.ga-wg-hinweis{margin:0 0 10px;font-size:10.5px;letter-spacing:.16em;text-transform:uppercase;font-weight:600;color:var(--ga-sek);display:flex;flex-wrap:wrap;justify-content:space-between;gap:4px 12px}
.ga-wg-status{letter-spacing:.06em;font-variant-numeric:tabular-nums}
.ga-wg-status[data-ok="true"]{color:var(--ga-text)}
.ga-wg{position:relative;display:flex;flex-direction:row;align-items:stretch;border:1px solid var(--ga-rand);border-radius:12px;overflow-x:auto;overflow-y:hidden;overscroll-behavior-x:contain;background:var(--ga-karte)}
.ga-wg[data-fehlt="true"]{border:1.5px solid var(--ga-text)}
.ga-wg-spalte{position:relative;display:flex;flex-direction:column;flex:1 1 0;min-width:0;padding:14px 16px;transition:background .15s;hyphens:auto;overflow-wrap:normal;word-break:normal}
.ga-wg[data-n="3"] .ga-wg-spalte,.ga-wg[data-n="4"] .ga-wg-spalte,.ga-wg[data-n="5"] .ga-wg-spalte,.ga-wg[data-n="6"] .ga-wg-spalte{min-width:150px}
label.ga-wg-spalte{cursor:pointer}
label.ga-wg-spalte:hover{background:var(--ga-tief)}
.ga-wg-spalte[data-an="true"]{background:var(--ga-seite)}
.ga-wg[data-gesperrt="true"] label.ga-wg-spalte{cursor:default}
.ga-wg[data-gesperrt="true"]{background:var(--ga-tief);border-color:var(--ga-linie)}
.ga-wg[data-gesperrt="true"] .ga-gericht{color:var(--ga-sek)}
.ga-wg[data-gesperrt="true"] label.ga-wg-spalte:hover{background:transparent}
.ga-wg-spalte:focus-within{outline:2px solid var(--ga-akzent);outline-offset:-2px}
.ga-wg-oder{position:relative;display:grid;place-content:center;flex:0 0 auto;color:var(--ga-sek);font-family:var(--ga-serif);font-style:italic;font-size:14px;padding:0 2px}
.ga-wg-oder::before{content:"";position:absolute;left:50%;top:0;bottom:0;width:1px;background:var(--ga-rand)}
.ga-wg-oder span{position:relative;background:var(--ga-karte);padding:8px 0}
.ga-wg[data-gesperrt="true"] .ga-wg-oder span{background:var(--ga-tief)}
.ga-wg-name{display:block}
.ga-wg .ga-gericht{font-size:18px;hyphens:auto;overflow-wrap:normal;word-break:normal;text-wrap:wrap}
.ga .ga-kreis{appearance:none;-webkit-appearance:none;display:inline-block;vertical-align:-3px;width:20px;height:20px;margin:0 10px 0 0;border:2px solid #8f8570;border-radius:50%;background:#fff;box-shadow:none;cursor:inherit;opacity:1}
.ga .ga-kreis:checked{border-color:var(--ga-akzent);background:var(--ga-akzent);box-shadow:inset 0 0 0 4px #fff}
.ga .ga-kreis:disabled{border-color:var(--ga-rand);background:var(--ga-hoch);opacity:1}
.ga .ga-kreis:focus-visible{outline:none}
.ga-wg-zaehler{margin-top:auto;padding-top:14px}
.ga-wg-zaehler .ga-zaehler-block{flex-wrap:wrap;row-gap:6px}
/* Freigabe Inhaber (Variante B, IMMER NEBENEINANDER): bei JEDER Breite nebeneinander; Gerichtnamen brechen in ihrer Spalte um. */
@media (max-width:520px){
  .ga-wg .ga-gericht{font-size:15px;hyphens:auto;overflow-wrap:normal;word-break:normal}
  .ga-wg-spalte{padding:12px 10px}
}
@media (max-width:360px){
  .ga-wg .ga-gericht{font-size:14px}
  .ga-wg-zaehler .ga-zaehler-block{gap:6px}
}
.ga-wg[data-menue="true"] .ga-wg-spalte{padding:16px 16px 0}
.ga-menue-art{display:inline-block;align-self:flex-start;margin:0 0 8px;padding:2px 10px;border-radius:999px;font-size:11.5px;font-weight:700;letter-spacing:.02em;background:var(--ga-tief);color:var(--ga-text);border:1px solid var(--ga-rand)}
.ga-menue-art[data-art="vegan"],.ga-menue-art[data-art="vegetarisch"]{background:#e3efe2;color:#2f6b3f;border-color:transparent}
.ga-menue-gaenge{display:grid;gap:8px;margin:8px 0 0;font-size:13.5px;line-height:1.45}
.ga-menue-gaenge small{display:block;font-size:10.5px;letter-spacing:.14em;text-transform:uppercase;color:var(--ga-sek);font-weight:700}
.ga-menue-gaenge p{margin:0;overflow-wrap:normal;hyphens:manual}
.ga-wg[data-menue="true"] .ga-gericht-aufpreis{margin-top:auto;padding-top:12px}
.ga-wg[data-menue="true"] .ga-gericht{font-size:19px}
.ga-menue-gaenge .ga-gericht-zusatz{font-size:inherit;color:inherit}
.ga-wg[data-menue="true"] .ga-menue-gaenge small{letter-spacing:.12em}
.ga-wg[data-menue="true"] .ga-wg-zaehler{box-sizing:border-box;margin:14px -16px 0;padding:10px 16px;background:var(--ga-tief);box-shadow:0 -1px 0 var(--ga-rand),15px 0 0 var(--ga-tief),-15px 0 0 var(--ga-tief),15px -1px 0 var(--ga-rand),-15px -1px 0 var(--ga-rand)}
.ga-menue-hinweis{margin:10px 0 0;font-size:12.5px;color:var(--ga-sek)}
.ga-menue-aktion{display:flex;flex-wrap:wrap;gap:10px 16px;align-items:center;justify-content:space-between;margin-top:14px}
.ga-menue-rest{font-weight:700;font-size:13px;border-radius:999px;padding:4px 12px;background:#f7ecd3;color:#8a5a0b;font-variant-numeric:tabular-nums}
.ga-menue-rest[data-ok="true"]{background:#e3efe2;color:#2f6b3f}
.ga-menue-knopf{height:44px;padding:0 20px;border-radius:12px;border:1.5px solid var(--ga-akzent);background:var(--ga-akzent);color:var(--ga-auf);font-family:var(--ga-sans);font-size:14px;font-weight:700;cursor:pointer}
.ga-menue-knopf[disabled]{background:var(--ga-hoch);border-color:var(--ga-hoch);color:#6f675a;cursor:not-allowed}
.ga-menue-block{padding:18px 24px 0;min-width:0;container-type:inline-size}
.ga[data-eigen="true"] .ga-menue-block{padding:0}
.ga-menue-solo{padding:0;margin:0}
@container (max-width:560px){
  .ga-wg[data-menue="true"]{flex-direction:column;overflow-x:hidden}
  .ga-wg[data-menue="true"] .ga-wg-spalte{flex:none}
  .ga-wg[data-menue="true"] .ga-wg-oder{padding:6px 0}
  .ga-wg[data-menue="true"] .ga-wg-zaehler{box-shadow:0 -1px 0 var(--ga-rand)}
  .ga-wg[data-menue="true"] .ga-wg-oder::before{left:0;right:0;top:50%;bottom:auto;width:auto;height:1px}
}
.ga-wunsch{margin:22px 24px 0;padding:22px 0 0;border-top:1px solid var(--ga-rand);min-width:0}
.ga-koerper>.ga-wunsch:first-child{margin-top:0;border-top:0}
.ga-wunsch-titel{font-family:var(--ga-serif);font-size:22px;font-weight:500;margin:0 0 2px}
.ga-wunsch-hinweis{margin:0;color:var(--ga-sek);font-size:13.5px}
.ga-zb-liste{list-style:none;margin:2px 0 0;padding:0}
.ga-zb{border:1px solid var(--ga-linie);border-radius:14px;padding:16px 16px 12px;background:var(--ga-tief);margin-top:12px;min-width:0}
.ga-zb[data-an="true"]{border-color:var(--ga-akzent);background:var(--ga-karte)}
.ga-zb-name{font-family:var(--ga-serif);font-size:19px;font-weight:500;line-height:1.25;margin:0;text-wrap:balance}
.ga-zb-preis{margin:4px 0 0;font-weight:700;font-size:13.5px;font-variant-numeric:tabular-nums}
.ga-zb-preis small{font-weight:500;color:var(--ga-sek);font-size:12.5px}
.ga-zb-erkl{margin:10px 0 4px;font-size:13px;line-height:1.5;color:var(--ga-sek)}
.ga-zb-erkl b{color:var(--ga-text)}
.ga-zb-desc{margin:6px 0 0;font-size:13.5px;line-height:1.55;color:var(--ga-sek)}
.ga-zb-details{margin:12px 0 0;padding:4px 12px;background:var(--ga-karte);border:1px solid var(--ga-linie);border-radius:10px;font-size:13.5px;color:var(--ga-sek)}
.ga-zb-details dl{margin:0}
.ga-zb-details .ga-zb-r{padding:9px 0;border-bottom:1px dotted var(--ga-linie)}
.ga-zb-details .ga-zb-r:last-child{border-bottom:0}
.ga-zb-details dt{font-size:10.5px;letter-spacing:.16em;text-transform:uppercase;font-weight:700;color:var(--ga-sek);margin:0 0 1px}
.ga-zb-details dd{margin:0;font-family:var(--ga-serif);font-size:16.5px;color:var(--ga-text);line-height:1.35}
.ga-zb-details dd small{display:block;font-family:var(--ga-sans);font-size:13px;color:var(--ga-sek);line-height:1.5;margin-top:2px}
.ga-zb-details ul{margin:0;padding:0;list-style:none}
.ga-zb-details li{padding:7px 0;border-bottom:1px dotted var(--ga-linie)}
.ga-zb-details li:last-child{border-bottom:0}
.ga-zb-status{margin:10px 0 0;font-size:13px;line-height:1.5;color:var(--ga-sek)}
.ga-zb-status[data-ok="true"]{color:var(--ga-text);font-weight:600}
.ga-zb-steuer{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:6px 14px;margin-top:12px;min-height:48px;padding-top:12px;border-top:1px solid var(--ga-linie)}
.ga-zb-links{display:flex;align-items:center;gap:12px;min-width:0}
.ga-zb-betrag{margin-left:auto;text-align:right;font-weight:700;font-size:14.5px;white-space:nowrap;font-variant-numeric:tabular-nums}
.ga-zb-betrag[data-leer="true"]{color:var(--ga-sek);font-weight:500}
.ga-zb-sperre{margin-left:auto;white-space:nowrap;font-size:13px;color:var(--ga-sek);display:inline-flex;align-items:center;gap:6px}
.ga-schalter{display:inline-flex;align-items:center;gap:12px;min-height:44px;border:0;background:none;padding:0;text-align:left;font-family:var(--ga-sans);font-weight:600;font-size:14px;color:var(--ga-text);cursor:pointer}
.ga-schalter:disabled{cursor:not-allowed}
.ga-schalter:disabled .ga-spur{opacity:.5}
.ga-spur{width:48px;height:28px;border-radius:999px;background:#8f8570;position:relative;flex:none;transition:background .15s}
.ga-spur::after{content:"";position:absolute;left:3px;top:3px;width:22px;height:22px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.25);transition:transform .15s}
.ga-schalter[aria-checked="true"] .ga-spur{background:var(--ga-akzent)}
.ga-schalter[aria-checked="true"] .ga-spur::after{transform:translateX(20px)}
.ga-zaehler{position:relative;display:inline-flex;align-items:center;height:44px;border:1px solid #8f8570;border-radius:12px;background:#fff;overflow:hidden;flex:none}
.ga-zaehler[data-an="true"]{border-color:var(--ga-akzent)}
.ga-zaehler button{width:44px;height:44px;border:0;background:none;font-size:22px;line-height:1;cursor:pointer;color:var(--ga-text);display:flex;align-items:center;justify-content:center;flex:none}
.ga-zaehler button:hover:not(:disabled){background:var(--ga-hoch)}
.ga-zaehler button:disabled{color:#b3aa97;cursor:not-allowed}
.ga-zaehler input{width:40px;height:44px;border:0;background:none;text-align:center;font-family:var(--ga-sans);font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;color:var(--ga-text);min-width:0;padding:0}
.ga-zaehler[data-an="true"] input{color:var(--ga-akzent)}
.ga-zaehler-block[data-aus="true"] .ga-zaehler{opacity:.5}
.ga-zaehler-einheit{font-weight:600;font-size:14px;white-space:nowrap}
.ga-zaehler-block{display:inline-flex;align-items:center;gap:12px}
.ga-kasten{background:var(--ga-karte);border:1px solid var(--ga-rand);border-radius:18px;padding:22px 22px 20px;display:none}
@media (min-width:768px){.ga-kasten{display:block;width:100%;max-width:520px;margin-left:auto}}
@media (min-width:1180px){.ga-kasten{position:sticky;top:16px;align-self:start;max-width:none;max-height:calc(100vh - 32px);overflow:auto}}
.ga[data-angenommen="true"] .ga-kasten{display:block;position:static;max-width:none;max-height:none;margin-left:0}
/* Nach der Annahme (Mockup „Zubuchen“, Antoine 31.10.2026): der Inhaltsrahmen kommt von der Seite (.gast-rahmen), kein
   eigenes Ausbrechen; eine einzelne Karte füllt die linke Spalte, der Kasten steht direkt daneben — keine leere Mitte. */
.ga[data-angenommen="true"]{margin-inline:0}
.ga[data-angenommen="true"] .ga-karten[data-n="1"]{grid-template-columns:minmax(0,1fr)}
@media (min-width:1180px){.ga[data-angenommen="true"] .ga-layout>.ga-kasten{position:sticky;top:16px}}
.ga[data-angenommen="true"] .ga-wg[data-lesen="true"] .ga-wg-spalte[data-an="false"]{opacity:.5}
.ga-angenommen{display:flex;align-items:flex-start;gap:10px;margin:0 0 14px;font-size:13.5px;line-height:1.45}
.ga-angenommen svg,.ga-angenommen .material-symbols-outlined{flex:none;color:var(--ga-akzent)}
.ga-zb[data-lesen="true"][data-an="false"]{opacity:.6}
.ga-zb-steuer[hidden]{display:none}
.ga-zb-gebucht{margin:10px 0 0;font-size:13.5px;font-weight:700;display:flex;align-items:center;gap:6px;color:var(--ga-text)}
.ga-zb[data-lesen="true"][data-an="false"] .ga-zb-gebucht{font-weight:500;color:var(--ga-sek)}
.ga-kasten h3{font-family:var(--ga-serif);font-size:24px;font-weight:500;margin:0 0 14px}
.ga-k-sub{font-size:13.5px;color:var(--ga-sek);margin:0}
.ga-k-liste{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;font-size:14px}
.ga-k-liste li{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:2px 14px;align-items:start;padding:10px 0;border-top:1px solid var(--ga-linie)}
.ga-k-links{display:flex;flex-direction:column;min-width:0}
.ga-k-liste li:first-child{border-top:0;padding-top:0}
.ga-k-name{font-family:var(--ga-serif);font-size:17px;font-weight:500;line-height:1.25;text-wrap:balance}
.ga-k-liste .ga-k-betrag{font-weight:700;font-variant-numeric:tabular-nums;text-align:right;white-space:nowrap}
.ga-k-liste .ga-k-sub2{display:block;font-size:12.5px;line-height:1.45;color:var(--ga-sek)}
.ga-k-liste .ga-k-name+.ga-k-sub2{margin-top:2px}
.ga-k-summe{border-top:1.5px solid var(--ga-text);margin-top:6px;padding-top:14px;display:flex;justify-content:space-between;align-items:baseline;gap:12px;font-variant-numeric:tabular-nums}
.ga-k-summe span:first-child{font-weight:700}
.ga-k-summe span:last-child{font-family:var(--ga-serif);font-size:32px;font-weight:500;line-height:1}
.ga-k-klein{font-size:12px;color:var(--ga-sek);margin:6px 0 0;font-variant-numeric:tabular-nums}
.ga-k-steuer{display:flex;justify-content:space-between;flex-wrap:wrap;gap:2px 12px;font-size:12px;color:var(--ga-sek);margin:6px 0 0;font-variant-numeric:tabular-nums}
.ga-k-leer{font-size:13.5px;color:var(--ga-sek);margin:0 0 16px}
.ga-k-leer+.ga-annehmen{margin-top:0}
.ga-k-zeile{display:flex;justify-content:space-between;align-items:baseline;gap:12px;font-size:13px;margin-top:14px;padding:12px 14px;background:var(--ga-tief);border-radius:10px;font-variant-numeric:tabular-nums}
.ga-annehmen{margin-top:16px;width:100%;height:48px;border-radius:12px;border:1.5px solid var(--ga-akzent);background:var(--ga-akzent);color:var(--ga-auf);font-family:var(--ga-sans);font-size:14.5px;font-weight:700;cursor:pointer}
.ga-annehmen[aria-disabled="true"]{background:var(--ga-hoch);border-color:var(--ga-hoch);color:#6f675a;cursor:not-allowed}
.ga-k-hinweis{font-size:12px;color:var(--ga-sek);margin:12px 0 0}
.ga-blatt-hinter{position:fixed;inset:0;background:rgba(38,33,25,.45);z-index:50;display:flex;align-items:flex-end}
.ga-blatt{background:var(--ga-karte);width:100%;max-height:85vh;overflow:auto;border-radius:18px 18px 0 0;padding:16px}
.ga-blatt .ga-kasten{display:block;position:static;border:0;padding:0;max-width:none;max-height:none}
.ga-blatt-zu{display:block;margin:0 0 8px auto;height:44px;padding:0 14px;border:1px solid var(--ga-linie);border-radius:12px;background:var(--ga-karte);font-size:14px;cursor:pointer}
@media (max-width:767px){
  .ga-teil{padding-left:18px;padding-right:18px}
  .ga-kopf{padding-top:20px}
  .ga-foto{height:150px}
  .ga-preis{padding-top:12px}
  .ga-gaeste{padding-bottom:14px}
  .ga-knopf-zelle{padding:0 18px 20px}
  .ga-gang{padding:16px 18px 0}
  .ga-sektion-titel{padding:20px 18px 0}
  .ga-wunsch{margin:20px 18px 0;padding-top:20px}
  .ga-karte{margin-bottom:18px}
}
.ga-zb-details dt.ga-gang-titel{display:block;letter-spacing:.16em;margin:0 0 1px}
.ga-zb-details dt.ga-gang-titel::after{display:none}
`;

/* ───────────── kleine Bausteine ───────────── */

function Schalter({ an, aktiv, label, text, onChange }: { an: boolean; aktiv: boolean; label: string; text?: string; onChange: (an: boolean) => void }) {
  return (
    <button type="button" role="switch" aria-checked={an} aria-label={label} disabled={!aktiv} className="ga-schalter" onClick={() => onChange(!an)}>
      <span className="ga-spur" />
      {text ? <span aria-hidden="true">{text}</span> : null}
    </button>
  );
}

export function Zaehler({
  wert,
  max,
  tippMax,
  aktiv,
  label,
  einheit,
  onChange,
  testId,
}: {
  wert: number;
  max: number;
  /** Obergrenze fuers EINTIPPEN (Plus-Knopf folgt `max`): im Wahlgang darf man ueber den Rest hinaus tippen, die Seite sagt dann „n Gaeste zu viel“. */
  tippMax?: number;
  aktiv: boolean;
  label: string;
  einheit?: string | null;
  onChange: (n: number) => void;
  testId?: string;
}) {
  const t = useT();
  const klemme = (n: number) => Math.max(0, Math.min(tippMax ?? max, Math.floor(Number.isFinite(n) ? n : 0)));
  return (
    <span className="ga-zaehler-block" data-testid={testId} data-aus={aktiv ? "false" : "true"}>
      <span className="ga-zaehler" role="group" aria-label={label} data-an={wert > 0 ? "true" : "false"}>
        <button type="button" aria-label={`${t.weniger}: ${label}`} disabled={!aktiv || wert <= 0} onClick={() => onChange(klemme(wert - 1))}>
          −
        </button>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          aria-label={label}
          disabled={!aktiv}
          value={String(wert)}
          onChange={(e) => {
            const ziffern = e.target.value.replace(/[^0-9]/g, "");
            onChange(klemme(ziffern === "" ? 0 : Number(ziffern)));
          }}
          onFocus={(e) => e.target.select()}
        />
        <button type="button" aria-label={`${t.mehr}: ${label}`} disabled={!aktiv || wert >= max} onClick={() => onChange(klemme(wert + 1))}>
          +
        </button>
      </span>
      {aktiv && einheit ? <span className="ga-zaehler-einheit">{einheit}</span> : null}
    </span>
  );
}

const TextKontext = createContext<AuswahlTexte>(AT.de);
function useT(): AuswahlTexte {
  return useContext(TextKontext);
}

/* ───────────── Wahlgang (oder) ───────────── */

/**
 * Weiche Trennstellen (U+00AD) vor gängigen Gastro-Wortteilen langer deutscher Komposita. `hyphens:auto` allein trennt nur,
 * wenn der Browser ein Wörterbuch hat (Linux/Headless nicht); so bricht „Rinderfiletmedaillons“ überall mit Bindestrich.
 */
const TRENNTEILE = /(?<=[a-zäöüß]{4})(medaillons?|reduktion|püree|pürees|sauce|soße|gemüse|filet|brust|steak|salat|suppe|creme|crème|torte|kuchen|auflauf|ragout|risotto|carpaccio|variation|spezialität|geflügel|kartoffel|pilz|trüffel|butter)(?=[a-zäöüß]|$)/gi;
export function weicheTrennung(text: string | null | undefined): string {
  if (!text) return "";
  return text.replace(/[A-Za-zÄÖÜäöüß]{12,}/g, (w) => w.replace(TRENNTEILE, "\u00AD$1"));
}


/**
 * Gestaltung 28.10.2026 (Variante B „Eine Gruppe“, vom Inhaber gewählt): EIN Rahmen um alle Alternativen, innen durch
 * eine Linie mit „oder“ geteilt. Bei zwei Alternativen und genug Breite stehen die Hälften nebeneinander, sonst
 * untereinander (Handy). Mengengenau (sql/755): unten in jeder Hälfte der Gäste-Zähler.
 */
function WahlgangBlock({
  wg,
  mengen,
  soll,
  aktiv,
  zeigeFehlt,
  name,
  beschreibung,
  aufpreis,
  onMenge,
  zeilen,
  lesen = false,
}: {
  wg: Wahlgang;
  /** Nach der Annahme: gespeicherte Verteilung als Text, kein Zähler. */
  lesen?: boolean;
  /** Die Zeilen der Speisekarte dieses Blocks (Gang, Art „Fisch“/„Fleisch“, Gerichtname) - wie im `EinzelwahlBlock`. */
  zeilen?: KartenZeile<PublicOfferItem>[];
  mengen: Mengen;
  soll: number;
  aktiv: boolean;
  zeigeFehlt: boolean;
  name: (it: PublicOfferItem) => string;
  beschreibung: (it: PublicOfferItem) => string | null;
  /** sql/755: Aufpreis je Person gegenüber der günstigsten Alternative (Cent), 0 = keiner. */
  aufpreis: (itemId: string) => number;
  onMenge: (gruppeId: string, itemId: string, n: number) => void;
}) {
  const t = useT();
  const stand = wahlgangStand(wg, mengen, soll);
  const reihe = mengen[wg.gruppeId] ?? {};
  const status = stand.zuViel > 0 ? t.wahlZuViel(stand.zuViel) : stand.ok ? t.wahlFertig(soll) : t.wahlOffen(stand.offen);
  const fehlt = zeigeFehlt && !stand.ok;
  return (
    <>
      <p className="ga-wg-hinweis">
        <span>{lesen ? t.wahlKopfGebucht(soll) : t.wahlKopf(soll)}</span>
        {!lesen && (
          <span className="ga-wg-status" data-ok={stand.ok ? "true" : "false"} aria-live="polite">
            {fehlt ? t.bitteWaehlen : status}
          </span>
        )}
      </p>
      <div className="ga-wg" data-testid={`ga-wahlgang-${wg.gruppeId}`} data-wahlgang={wg.gruppeId} data-n={wg.gerichte.length} data-zaehler="true" data-lesen={lesen ? "true" : "false"} data-fehlt={fehlt ? "true" : "false"} data-gesperrt={aktiv ? "false" : "true"} lang={t.oder === "or" ? "en" : "de"} role="group" aria-label={wg.gang ?? t.wahlKopf(soll)}>
        {wg.gerichte.map((g, i) => {
          const wert = Math.max(0, Math.floor(reihe[g.id] ?? 0));
          // Nie mehr als der Rest des Solls - und nie mehr, als die Alternative Gäste hat (F-B5, wie SQL).
          const max = Math.max(0, Math.min(g.max, soll - (stand.summe - wert)));
          // V-1262: lange Freitexte wie in der Karte teilen (Gerichtname groß, Rest klein); der Gang steht schon über dem Block.
          // Die Speisekarten-Zeile kennt zusätzlich die Art („Fisch“ bei „Hauptgericht Fisch: …“) - wie im EinzelwahlBlock.
          const zl = zeilen?.find((z) => z.it.id === g.id);
          const teil = zl ? zl.teile : gerichtTeile(name(g.item), beschreibung(g.item), wg.gang);
          const text = teil.titel ? teil.text : null;
          return (
            <Fragment key={g.id}>
              {i > 0 && (
                <div className="ga-wg-oder" aria-hidden="true">
                  <span>{t.oder}</span>
                </div>
              )}
              <div className="ga-wg-spalte" data-testid={`ga-wg-spalte-${g.id}`} data-an={wert > 0 ? "true" : "false"}>
                {zl?.art && (
                  <p className="ga-gericht-art" data-testid={`ga-wg-art-${g.id}`}>
                    {zl.art}
                  </p>
                )}
                <p className="ga-gericht">{weicheTrennung(teil.titel ?? teil.text)}</p>
                {text && <p className="ga-gericht-text">{text}</p>}
                {aufpreis(g.id) > 0 && (
                  <p className="ga-gericht-aufpreis" data-testid={`ga-aufpreis-${g.id}`}>
                    {t.aufpreisPP(money(aufpreis(g.id)))}
                  </p>
                )}
                <div className="ga-wg-zaehler">
                  {lesen ? (
                    <p className="ga-gericht-text" data-testid={`ga-wg-gebucht-${g.id}`} style={{ margin: 0, fontWeight: wert > 0 ? 700 : 400 }}>
                      {wert > 0 ? t.gaeste(wert) : "–"}
                    </p>
                  ) : aktiv ? (
                    <Zaehler wert={wert} max={max} tippMax={soll} aktiv label={t.anzahlFuer(name(g.item))} einheit={t.gaeste(wert).replace(/^\d+\s*/, "")} onChange={(n) => onMenge(wg.gruppeId, g.id, n)} testId={`ga-zaehler-${g.id}`} />
                  ) : (
                    <Zaehler wert={0} max={0} aktiv={false} label={t.anzahlFuer(name(g.item))} onChange={() => undefined} testId={`ga-zaehler-${g.id}`} />
                  )}
                </div>
              </div>
            </Fragment>
          );
        })}
      </div>
    </>
  );
}

/* ───────────── Menüwahl im Paket (sql/771/772) ───────────── */

/** Datum JJJJ-MM-TT als „TT.MM.JJJJ“ (de) bzw. „DD/MM/YYYY“ (en). */
export function menueDatum(iso: string | null | undefined, lang: "de" | "en"): string | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return d.toLocaleDateString(lang === "en" ? "en-GB" : "de-DE", { timeZone: "UTC", day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * Variante B für ganze Menüs (Freigabe Antoine 01.11.2026, Mockup „Menüwahl im Paket“): EIN Rahmen, in jeder Spalte ein
 * vollständiges Menü mit Art, Gängen, Aufpreis und Gäste-Zähler; oben „Noch n Gäste zuordnen“. Auf dem Handy untereinander.
 * Der Block rechnet nichts: Aufpreise kommen eingefroren vom Server, die Summe der Karte aus der Preisliste der Fassung.
 */
function MenuewahlBlock({
  mw,
  mengen,
  aktiv,
  zeigeFehlt,
  name,
  beschreibung,
  onMenge,
  lesen = false,
  spaeterHinweis = null,
}: {
  mw: Menuewahl;
  mengen: Mengen;
  aktiv: boolean;
  zeigeFehlt: boolean;
  name: (it: PublicOfferItem) => string;
  beschreibung: (it: PublicOfferItem) => string | null;
  onMenge: (gruppeId: string, itemId: string, n: number) => void;
  /** Nach der Annahme: nur die gespeicherte Verteilung, kein Zähler. */
  lesen?: boolean;
  /** Text unter dem Block („Sie können die Menüs bis … festlegen“), wenn die Wahl später möglich ist. */
  spaeterHinweis?: string | null;
}) {
  const t = useT();
  const lage = menuewahlLage(mw, mengen);
  const reihe = mengen[mw.gruppeId] ?? {};
  const fehlt = zeigeFehlt && menuewahlSperrt(mw, lage);
  const status = lage.zuViel > 0 ? t.menueZuViel(lage.zuViel) : lage.ok ? t.menueFertig(mw.soll) : t.menueOffen(lage.offen);
  return (
    <div className="ga-menue-block" data-testid={`ga-menuewahl-${mw.gruppeId}`} data-menuewahl={mw.gruppeId}>
      <p className="ga-wg-hinweis">
        <span>{lesen ? t.menueKopfGebucht(mw.soll) : t.menueKopf(mw.soll)}</span>
        <span className="ga-wg-status" data-ok={lage.ok ? "true" : "false"} aria-live="polite" data-testid={`ga-menue-rest-${mw.gruppeId}`}>
          {lesen ? (lage.ok ? null : t.menueNichtFestgelegt) : fehlt ? t.bitteWaehlen : status}
        </span>
      </p>
      <div className="ga-wg" data-menue="true" data-n={mw.menues.length} data-zaehler="true" data-lesen={lesen ? "true" : "false"} data-fehlt={fehlt ? "true" : "false"} data-gesperrt={aktiv ? "false" : "true"} lang={t.oder === "or" ? "en" : "de"} role="group" aria-label={t.menueKopf(mw.soll)}>
        {mw.menues.map((m, i) => {
          const wert = Math.max(0, Math.floor(reihe[m.id] ?? 0));
          const max = Math.max(0, Math.min(mw.soll, mw.soll - (lage.summe - wert)));
          const zeilen = speisekarteZeilen(m.bestandteile, name, beschreibung);
          const artText = m.art ? (t.menueArt[m.art] ?? m.art) : null;
          return (
            <Fragment key={m.id}>
              {i > 0 && (
                <div className="ga-wg-oder" aria-hidden="true">
                  <span>{t.oder}</span>
                </div>
              )}
              <div className="ga-wg-spalte" data-testid={`ga-menue-${m.id}`} data-an={wert > 0 ? "true" : "false"}>
                {artText && (
                  <span className="ga-menue-art" data-art={m.art ?? ""} data-testid={`ga-menue-art-${m.id}`}>
                    {artText}
                  </span>
                )}
                <p className="ga-gericht">{weicheTrennung(name(m.item))}</p>
                <div className="ga-menue-gaenge">
                  {zeilen.map((zl) => {
                    const { allergene, dietText } = allergeneUndDiaet(zl.it, t.oder === "or" ? "en" : "de");
                    return (
                      <div key={zl.it.id} data-testid={`ga-menue-gang-${zl.it.id}`}>
                        {zl.courseLabel && <small>{zl.courseLabel}</small>}
                        <p>
                          {weicheTrennung(zl.teile.titel ?? zl.teile.text)}
                          {zl.teile.titel && zl.teile.text ? <span className="ga-gericht-zusatz">{/^mit\s/i.test(zl.teile.text) ? " " : " – "}{zl.teile.text}</span> : null}
                          {(allergene || dietText) && (
                            <span className="ga-gericht-zusatz ga-menue-lmiv" data-testid={`ga-menue-lmiv-${zl.it.id}`}>
                              {allergene}
                              {dietText}
                            </span>
                          )}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <p className="ga-gericht-aufpreis" data-testid={`ga-menue-aufpreis-${m.id}`}>
                  {m.aufpreisCents > 0 ? t.menueAufpreis(money(m.aufpreisCents)) : t.menueOhneAufpreis}
                </p>
                <div className="ga-wg-zaehler">
                  {lesen ? (
                    <p className="ga-gericht-text" data-testid={`ga-menue-gebucht-${m.id}`} style={{ margin: 0, fontWeight: wert > 0 ? 700 : 400 }}>
                      {wert > 0 ? t.gaeste(wert) : "–"}
                    </p>
                  ) : aktiv ? (
                    <Zaehler wert={wert} max={max} tippMax={mw.soll} aktiv label={t.anzahlFuer(name(m.item))} einheit={t.menueGaeste} onChange={(n) => onMenge(mw.gruppeId, m.id, n)} testId={`ga-menue-zaehler-${m.id}`} />
                  ) : (
                    <Zaehler wert={0} max={0} aktiv={false} label={t.anzahlFuer(name(m.item))} onChange={() => undefined} testId={`ga-menue-zaehler-${m.id}`} />
                  )}
                </div>
              </div>
            </Fragment>
          );
        })}
      </div>
      {!lesen && (
        <p className="ga-menue-hinweis" data-testid={`ga-menue-regel-${mw.gruppeId}`}>
          {mw.spaeter && spaeterHinweis ? spaeterHinweis : t.menueRegel(mw.soll)}
        </p>
      )}
    </div>
  );
}

/** Die Menüwahlen einer Variante in der Karte (leer = keine Wahl). */
function MenuewahlBloecke({ p, o, aktiv, zeigeFehlt, lesen, name, beschreibung, lang }: { p: AuswahlProps; o: AngebotOption; aktiv: boolean; zeigeFehlt: boolean; lesen: boolean; name: (it: PublicOfferItem) => string; beschreibung: (it: PublicOfferItem) => string | null; lang: "de" | "en" }) {
  const t = AT[lang];
  const frist = menueDatum(menuewahlStandardFrist(p.offer.event.event_date), lang);
  const spaeterHinweis = frist ? t.menueSpaeterBis(frist) : t.menueSpaeterOhneDatum;
  return (
    <>
      {menuewahlenAus(o).map((mw) => (
        <MenuewahlBlock key={mw.gruppeId} mw={mw} mengen={p.mengen} aktiv={aktiv} zeigeFehlt={zeigeFehlt} name={name} beschreibung={beschreibung} onMenge={p.onMenge} lesen={lesen} spaeterHinweis={spaeterHinweis} />
      ))}
    </>
  );
}

/**
 * Die Menüwahl NACH der Buchung als eigene Ansicht (Link aus der Mail „Menüauswahl“): dieselben Karten mit Zählern, ohne
 * Rest der Angebotsseite. Eigenes Gewand (`.ga`), damit sie auch außerhalb der Auswahl gleich aussieht.
 */
export function MenuewahlGastAnsicht({
  lang,
  markenfarbe,
  option,
  mengen,
  onMenge,
  aktiv,
  name,
  beschreibung,
}: {
  lang: "de" | "en";
  markenfarbe: string | null | undefined;
  option: AngebotOption;
  mengen: Mengen;
  onMenge: (gruppeId: string, itemId: string, n: number) => void;
  aktiv: boolean;
  name: (it: PublicOfferItem) => string;
  beschreibung: (it: PublicOfferItem) => string | null;
}) {
  const t = AT[lang];
  const { akzent, auf } = akzentFarben(markenfarbe);
  const stil = { "--ga-akzent": akzent, "--ga-auf": auf, marginInline: 0 } as CSSProperties;
  return (
    <TextKontext.Provider value={t}>
      <div className="ga" style={stil} data-eigen="true" data-testid="menuewahl-gast">
        <style>{STIL_CSS}</style>
        {menuewahlenAus(option).map((mw) => (
          <MenuewahlBlock key={mw.gruppeId} mw={mw} mengen={mengen} aktiv={aktiv} zeigeFehlt={false} name={name} beschreibung={beschreibung} onMenge={onMenge} spaeterHinweis={null} />
        ))}
      </div>
    </TextKontext.Provider>
  );
}

/* ───────────── Wahlgruppe ohne Gäste-Verzeichnis („pro Tafel“) ───────────── */

/**
 * V-1262: eine „oder“-Gruppe, für die der Versand KEIN Gäste-Verzeichnis eingefroren hat (Option ohne
 * mengengenaue Wahlgänge). Der Server nimmt dafür keine Gäste je Gericht an (sql/755), wohl aber EINE Wahl
 * je Gruppe (Punkt 61, `menu-wahl`). Variante B: ein Rahmen, ganze Hälfte klickbar, Auswahlkreis vor dem Namen.
 */
function EinzelwahlBlock({
  gruppeId,
  gang,
  zeilen,
  gewaehlt,
  aktiv,
  onWahl,
  lesen = false,
}: {
  lesen?: boolean;
  gruppeId: string;
  gang: string | null;
  zeilen: KartenZeile<PublicOfferItem>[];
  gewaehlt: string | undefined;
  aktiv: boolean;
  onWahl: (gruppeId: string, itemId: string) => void;
}) {
  const t = useT();
  return (
    <>
      <p className="ga-wg-hinweis">{lesen ? t.einzelHinweisGebucht : t.einzelHinweis}</p>
      <div className="ga-wg" data-testid={`ga-einzelwahl-${gruppeId}`} data-lesen={lesen && gewaehlt != null ? "true" : "false"} data-n={zeilen.length} data-gesperrt={aktiv ? "false" : "true"} lang={t.oder === "or" ? "en" : "de"} role="radiogroup" aria-label={gang ?? t.einzelHinweis}>
        {zeilen.map((zl, i) => (
          <Fragment key={zl.it.id}>
            {i > 0 && (
              <div className="ga-wg-oder" aria-hidden="true">
                <span>{t.oder}</span>
              </div>
            )}
            <label className="ga-wg-spalte" data-testid={`ga-wg-spalte-${zl.it.id}`} data-an={gewaehlt === zl.it.id ? "true" : "false"}>
              {zl.art && <span className="ga-gericht-art ga-wg-name">{zl.art}</span>}
              <span className="ga-gericht ga-wg-name" data-testid={`ga-opt-${zl.it.id}`}>
                <Radio className="ga-kreis" name={`ga-einzel-${gruppeId}`} value={zl.it.id} checked={gewaehlt === zl.it.id} disabled={!aktiv} onChange={() => onWahl(gruppeId, zl.it.id)} aria-label={zl.teile.titel ?? zl.it.name} />
                {weicheTrennung(zl.teile.titel ?? zl.teile.text)}
              </span>
              {zl.teile.titel && zl.teile.text && <span className="ga-gericht-text ga-wg-name">{zl.teile.text}</span>}
            </label>
          </Fragment>
        ))}
      </div>
    </>
  );
}

/** Alle „oder“-Gruppen einer Variante, wie die Karte sie zeigt (Gang aus der Speisekarte, ohne Zubuchungen und Paket-Gänge). */
function wahlGruppenAnzeige(o: AngebotOption, name: (it: PublicOfferItem) => string, text: (it: PublicOfferItem) => string | null) {
  const buchbar = new Set(zubuchungenAus(o).map((z) => z.id));
  const { verschoben } = paketBestandteile(o.items ?? [], buchbar);
  const its = (o.items ?? []).filter((it) => it.section !== "adjustment" && !buchbar.has(it.id) && !verschoben.has(it.id));
  const liste: { gruppeId: string; gang: string | null; zeilen: KartenZeile<PublicOfferItem>[] }[] = [];
  for (const g of gaengeBloecke(speisekarteZeilen(its, name, text)))
    for (const b of alternativenBloecke(g.lines)) {
      const id = b.alternativen ? (b.lines[0]?.choiceGroupId ?? null) : null;
      if (id) liste.push({ gruppeId: id, gang: g.label, zeilen: b.lines });
    }
  return liste;
}

/** Die Wahlgruppen „pro Tafel“ einer Variante (ohne eingefrorenes Gäste-Verzeichnis). */
function einzelwahlGruppen(o: AngebotOption, name: (it: PublicOfferItem) => string, text: (it: PublicOfferItem) => string | null) {
  const verzeichnis = new Set(wahlgaengeAus(o).map((w) => w.gruppeId));
  return wahlGruppenAnzeige(o, name, text).filter((g) => !verzeichnis.has(g.gruppeId));
}

/**
 * Was eine ersetzende Zubuchung gegenüber dem Hauptmenü ausmacht, aus den Namen abgeleitet: „Menü 4 Gänge Winter
 * Edition Vegetarisch“ bei der Karte „Menü 4 Gänge Winter Edition“ → „Vegetarisch“. Nur ein kurzer Rest (höchstens zwei
 * Wörter) zählt, sonst `null` (dann steht der ganze Name da).
 */
export function ersetztArtAus(name: string, titel: string | null | undefined): string | null {
  const n = name.replace(/\s+/g, " ").trim();
  const t = (titel ?? "").replace(/\s+/g, " ").trim();
  if (!t || n.length <= t.length || n.slice(0, t.length).toLocaleLowerCase("de-DE") !== t.toLocaleLowerCase("de-DE")) return null;
  const rest = n.slice(t.length).replace(/^[\s\-–—:·,]+/, "").trim();
  if (!rest || rest.split(" ").length > 2) return null;
  return rest;
}

/** Wahlgänge mit Gäste-Zähler für „Ihre Auswahl“: „Hauptgericht: Filet …“ (alle gleich) oder „22 × Filet …, 3 × Brasato …“. */
function wahlgangZeilen(o: AngebotOption, mengen: Mengen, menuGaeste: number, name: (it: PublicOfferItem) => string, text: (it: PublicOfferItem) => string | null, ersatzGang: string): { id: string; text: string }[] {
  const anzeige = new Map(wahlGruppenAnzeige(o, name, text).map((g) => [g.gruppeId, g]));
  const out: { id: string; text: string }[] = [];
  for (const w of wahlgaengeAus(o)) {
    const reihe = mengen[w.gruppeId] ?? {};
    const a = anzeige.get(w.gruppeId);
    const titel = (id: string, it: PublicOfferItem) => {
      const zl = a?.zeilen.find((z) => z.it.id === id);
      return zl?.teile.titel ?? name(it);
    };
    const mit = w.gerichte.filter((g) => (reihe[g.id] ?? 0) > 0);
    if (mit.length === 0) continue;
    const soll = wahlSoll(w, menuGaeste, o.guest_count);
    const inhalt = mit.length === 1 && (reihe[mit[0]!.id] ?? 0) === soll ? titel(mit[0]!.id, mit[0]!.item) : mit.map((g) => `${reihe[g.id]} × ${titel(g.id, g.item)}`).join(", ");
    out.push({ id: w.gruppeId, text: `${w.gang ?? a?.gang ?? ersatzGang}: ${inhalt}` });
  }
  return out;
}

/* ───────────── Zubuchung („Auf Wunsch dazu“) ───────────── */

/** Beschreibung einer Zubuchung: Fließtext als Absatz, Aufzählungen als Liste im hellen Kasten. */
function ZubuchBeschreibung({ text, einleitung = null }: { text: string; einleitung?: string | null }) {
  const { punkte, rest } = aufzaehlungAus(text);
  if (punkte.length === 0) return <p className="ga-zb-desc">{text}</p>;
  return (
    <>
      {einleitung && <p className="ga-zb-desc">{einleitung}</p>}
      <div className="ga-zb-details">
        <ul>
          {punkte.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
      </div>
      {rest && <p className="ga-zb-desc">{rest}</p>}
    </>
  );
}

function ZubuchZeile({
  s,
  o,
  wahl,
  r,
  aktiv,
  nurAnsicht,
  name,
  beschreibung,
  onWahl,
  gaenge,
  lesen = false,
  verbindlich = true,
  neutral = false,
}: {
  lesen?: boolean;
  verbindlich?: boolean;
  neutral?: boolean;
  s: ZubuchSchalter;
  /** V-1262: die Gänge eines zubuchbaren Pakets (Bestandteile mit derselben package_id). */
  gaenge: PublicOfferItem[];
  o: AngebotOption;
  wahl: Wahl;
  r: Rechnung;
  aktiv: boolean;
  /** Vorschau/abgelaufen: keine Sperr-Zeile „Erst Menü wählen“ (bedienen kann hier niemand). */
  nurAnsicht: boolean;
  name: (it: PublicOfferItem) => string;
  beschreibung: (it: PublicOfferItem) => string | null;
  onWahl: (aenderung: Wahl) => void;
}) {
  const t = useT();
  const erstes = s.members[0]!;
  const weitere = s.members.slice(1);
  const eingeschaltet = s.members.some((m) => menge(wahl, m) > 0);
  const einzelnerZaehler = s.members.length === 1 && erstes.art === "zaehler";
  const titel = name(erstes.item);
  const betrag = s.members.reduce((sum, m) => sum + zeilenbetrag(m, wahl), 0) + (s.mindestumsatz && eingeschaltet ? r.aufstockungCents : 0);
  const gaesteZeile = (m: Zubuch) => (m.item.guest_count != null && m.item.guest_count > 0 ? m.item.guest_count : o.guest_count);
  // Wie viele Gäste ersetzen die ANDEREN Zubuchungen schon (Obergrenze dieses Zählers).
  const ersetztAndere = r.ersetzt - s.members.reduce((sum, m) => sum + (m.ersetzt ? (m.art === "alle" ? (menge(wahl, m) > 0 ? (m.ersetztGaeste ?? 0) : 0) : menge(wahl, m)) : 0), 0);
  const maxZaehler = erstes.ersetzt ? Math.max(0, Math.min(erstes.max, o.guest_count - ersetztAndere)) : erstes.max;
  const ersetzendMenge = s.members.some((m) => m.ersetzt) ? r.ersetzt : 0;
  const hatErsetzend = s.members.some((m) => m.ersetzt);

  function schalte(an: boolean) {
    const neu: Wahl = {};
    for (const m of s.members) neu[m.id] = an ? 1 : 0;
    onWahl(neu);
  }

  /** Kopfzeile wie im Entwurf: „+ 72,00 €“ fett, die Einheit klein dahinter. */
  const preisKopf = (m: Zubuch): ReactNode => {
    if (m.art === "mindestumsatz") return t.mindestumsatzInkl(money(m.cents));
    const pp = m.item.unit_price_cents;
    const [betrag, einheit] =
      m.art === "alle"
        ? pp > 0
          ? [pp, t.proPerson]
          : [m.cents, t.fuerAlle(gaesteZeile(m))]
        : m.art === "zaehler"
          ? [m.cents, m.ersetzt ? `${t.proPerson}, ${t.stattHauptmenue}` : m.gaesteCents ? t.proPerson : m.item.unit ? `/ ${m.item.unit}` : t.jeStueck]
          : [m.cents, t.pauschal];
    return (
      <>
        + {money(betrag)} <small>{einheit}</small>
      </>
    );
  };
  const preisText = (m: Zubuch): string => {
    if (m.art === "mindestumsatz") return t.mindestumsatzInkl(money(m.cents));
    if (m.art === "alle") {
      const pp = m.item.unit_price_cents;
      return `${pp > 0 ? `${money(pp)} ${t.proPerson} · ` : ""}${t.fuerAlle(gaesteZeile(m))} · ${money(m.cents)}`;
    }
    // Ersetzt die Zubuchung (auch der Gäste-Zähler aus sql/759) das Hauptmenü, zählt der Zähler Gäste - also „pro Person“, nicht „je Einheit“.
    // sql/763: auch der Gäste-Zähler einer nicht ersetzenden per_person-Zubuchung zählt Gäste - „pro Person“.
    if (m.art === "zaehler") return `${money(m.cents)} ${m.ersetzt || m.gaesteCents ? t.proPerson : m.item.unit ? `/ ${m.item.unit}` : t.jeStueck}`;
    return `${money(m.cents)} ${t.pauschal}`;
  };

  // Inhalt: die Gänge eines zubuchbaren Pakets, die übrigen „und“-verbundenen Zubuchungen mit ihrem Betrag.
  const gangZeilen = gaenge.length > 0 ? speisekarteZeilen(gaenge, name, beschreibung) : [];
  const text = beschreibung(erstes.item);
  const allergene = s.members.filter((m) => Array.isArray(m.item.allergens) && m.item.allergens.length > 0);

  const steuerText = erstes.art === "alle" ? t.fuerAlleSchalter(gaesteZeile(erstes)) : t.hinzufuegen;

  return (
    <li className="ga-zb" data-zaehler={einzelnerZaehler ? "true" : "false"} data-lesen={lesen ? "true" : "false"} data-testid={`ga-zubuchung-${erstes.id}`} data-an={eingeschaltet ? "true" : "false"}>
      <p className="ga-zb-name">{titel}</p>
      <p className="ga-zb-preis">{preisKopf(erstes)}</p>
      {text && <ZubuchBeschreibung text={text} einleitung={erstes.art === "alle" ? t.getraenkeEinleitung : null} />}
      {gangZeilen.length > 0 && (
        <div className="ga-zb-details" data-testid={`ga-zb-gaenge-${erstes.id}`}>
          <dl>
            {gangZeilen.map((zl) => (
              <div key={zl.it.id} className="ga-zb-r">
                {zl.courseLabel && <dt className="ga-gang-titel">{zl.courseLabel}</dt>}
                <dd>
                  {zl.teile.titel ?? null}
                  {zl.teile.text && (zl.teile.titel ? <small>{zl.teile.text}</small> : <small style={{ marginTop: 0 }}>{zl.teile.text}</small>)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      {weitere.length > 0 && (
        <div className="ga-zb-details" data-testid={`ga-zb-weitere-${erstes.id}`}>
          <dl>
            {weitere.map((m) => (
              <div key={m.id} className="ga-zb-r">
                <dt>{t.zusaetzlich}</dt>
                <dd>
                  {name(m.item)}
                  <small>
                    {preisText(m)}
                    {s.mindestumsatz && m.art !== "mindestumsatz" ? ` · ${t.zaehltNichtMu}` : ""}
                  </small>
                  {/* Review #1858: Beschreibung jedes weiteren Mitglieds (z. B. Bedingungen der Raummiete). */}
                  {beschreibung(m.item) ? (
                    <small className="ga-zb-weitere-text" data-testid={`ga-zb-weitere-text-${m.id}`}>
                      {beschreibung(m.item)}
                    </small>
                  ) : null}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      {allergene.map((m) => (
        <p key={`a-${m.id}`} className="ga-zb-desc">
          ({[...(m.item.allergens ?? [])].sort().join(", ")})
        </p>
      ))}
      {s.mindestumsatz && eingeschaltet && (
        <p className="ga-zb-status" data-ok={r.aufstockungCents === 0 ? "true" : "false"} data-testid={`ga-mindestumsatz-${s.mindestumsatz.id}`}>
          {r.aufstockungCents > 0 ? t.mindestumsatzIst(money(r.mindestumsatzIst), money(r.aufstockungCents)) : t.mindestumsatzErreicht}
        </p>
      )}
      {einzelnerZaehler && erstes.gaesteCents && !erstes.ersetzt && (
        <p className="ga-zb-erkl" data-testid={`ga-frage-${erstes.id}`}>
          {t.fuerWieviele}
        </p>
      )}
      {lesen && (
        <div className="ga-zb-steuer">
          <span className="ga-zb-gebucht" data-testid={`ga-gebucht-${erstes.id}`} style={{ margin: 0 }}>
            {eingeschaltet && <Icon name="check" className="text-[16px]" />}
            {eingeschaltet
              ? `${neutral ? t.gewaehltKurz : verbindlich ? t.verbindlichGebucht : t.angenommenKurz}${
                  erstes.art === "zaehler" ? ` · ${t.gaeste(menge(wahl, erstes))}` : erstes.art === "alle" ? ` · ${t.gaeste(gaesteZeile(erstes))}` : ""
                }`
              : t.nichtGebucht}
          </span>
          <span className="ga-zb-betrag" data-leer={!eingeschaltet ? "true" : "false"} data-testid={`ga-betrag-${erstes.id}`}>
            {eingeschaltet && betrag >= 0 ? `+ ${money(betrag)}` : "–"}
          </span>
        </div>
      )}
      <div className="ga-zb-steuer" hidden={lesen}>
        <span className="ga-zb-links">
          {einzelnerZaehler ? (
            <Zaehler
              wert={menge(wahl, erstes)}
              max={maxZaehler}
              aktiv={aktiv}
              label={t.anzahlZubuch(titel)}
              einheit={erstes.ersetzt || erstes.gaesteCents ? t.gaeste(menge(wahl, erstes)).replace(/^\d+\s*/, "") : erstes.item.unit}
              onChange={(n) => onWahl({ [erstes.id]: n })}
              testId={`ga-zaehler-${erstes.id}`}
            />
          ) : (
            <Schalter an={eingeschaltet} aktiv={aktiv} label={t.schalterAn(s.members.map((m) => name(m.item)).join(" + "))} text={steuerText} onChange={schalte} />
          )}
        </span>
        {!aktiv && !nurAnsicht ? (
          <span className="ga-zb-sperre" data-testid={`ga-sperre-${erstes.id}`}>
            <Icon name="lock" className="text-[15px]" />
            {t.erstMenue}
          </span>
        ) : (
          <span className="ga-zb-betrag" data-leer={!eingeschaltet ? "true" : "false"} data-testid={`ga-betrag-${erstes.id}`}>
            {eingeschaltet && betrag >= 0 ? `+ ${money(betrag)}` : "–"}
          </span>
        )}
      </div>
      {hatErsetzend && aktiv && ((eingeschaltet && ersetzendMenge > 0) || erstes.art === "zaehler") && (
        <p className="ga-zb-erkl" data-testid={`ga-statt-${erstes.id}`}>
          {eingeschaltet && ersetzendMenge > 0
            ? (() => {
                const art = ersetztArtAus(titel, o.title);
                const wer = art && /isch$/i.test(art) ? t.dasMenue(art.toLocaleLowerCase("de-DE")) : `„${titel}“`;
                const e = t.ersetztErklaerung(wer, o.title || t.menue, Math.max(0, o.guest_count - r.ersetzt));
                return (
                  <>
                    {e.vor}
                    <b>{e.fett}</b>
                    {e.nach}
                    <b>{e.zahl}</b>
                    {e.ende}
                  </>
                );
              })()
            : (() => {
                const e = t.ersetztErklaerungLeer(o.title || t.menue, maxZaehler);
                return (
                  <>
                    {e.vor}
                    <b>{e.fett}</b>
                    {e.nach}
                  </>
                );
              })()}
        </p>
      )}
    </li>
  );
}

/* ───────────── Kasten „Ihre Auswahl“ ───────────── */

export interface AnzahlungAnzeige {
  /** `null`, solange der Server keinen Betrag zur aktuellen Auswahl geliefert hat. */
  cents: number | null;
  vollzahlung: boolean;
  spaeter: boolean;
  /** Satz der prozentualen Anzahlung in Basispunkten (3000 = 30 %), NUR vom Server; `null`/fehlt bei Festbetrag. */
  prozentBp?: number | null;
}

function AuswahlKasten({
  o,
  wahl,
  r,
  mengen,
  name,
  anzahlung,
  annehmenSperre,
  onAnnehmen,
  annehmenAktiv,
  annehmenText,
  vorschau,
  alc,
  einzel = [],
  wahlgaenge = [],
  proPerson = null,
  angenommen = false,
  angenommenZeile,
  serverGesamtCents = null,
}: {
  angenommen?: boolean;
  angenommenZeile?: string;
  serverGesamtCents?: number | null;
  alc: boolean;
  /** Getroffene Wahl „pro Tafel“, z. B. „Hauptgericht: Filet …“. */
  einzel?: string[];
  /** Wahlgänge mit Gäste-Zähler, fertig formuliert („Hauptgericht: Filet …“). */
  wahlgaenge?: { id: string; text: string }[];
  /** Preis je Person der Karte (vom Server/aus dem Angebot), nur für „22 Gäste × 82,00 €“. */
  proPerson?: number | null;
  o: AngebotOption | null;
  wahl: Wahl;
  r: Rechnung | null;
  mengen: Mengen;
  name: (it: PublicOfferItem) => string;
  anzahlung: AnzahlungAnzeige | null;
  annehmenSperre: string | null;
  onAnnehmen: () => void;
  annehmenAktiv: boolean;
  annehmenText: string;
  vorschau: boolean;
}) {
  const t = useT();
  if (!o || !r) {
    // Entwurf: auch leer steht der Knopf da (deaktiviert), damit der Gast weiß, wohin die Auswahl führt.
    return (
      <aside className="ga-kasten" data-testid="ga-kasten" aria-label={t.kastenTitel}>
        <h3>{t.kastenTitel}</h3>
        <p className="ga-k-leer">{t.kastenLeer}</p>
        <button type="button" className="ga-annehmen" aria-disabled="true" data-testid="ga-annehmen" onClick={onAnnehmen}>
          {annehmenText}
        </button>
      </aside>
    );
  }
  const zub = zubuchungenAus(o).filter((z) => menge(wahl, z) > 0 && z.art !== "mindestumsatz");
  const mu = zubuchungenAus(o).find((z) => z.art === "mindestumsatz" && menge(wahl, z) > 0);
  const ust = ustAufteilung(o, wahl, r, mengen);
  const titel = o.title || o.menu_selection?.title || null;
  // „22 Gäste × 82,00 €“ nur, wenn die Zeile genau so aufgeht (sonst, z. B. bei Aufpreisen je Gericht, nur die Gäste).
  const menuMal = proPerson != null && proPerson > 0 && proPerson * r.menuGaeste === r.menuCents ? t.gaesteMal(r.menuGaeste, money(proPerson)) : t.gaeste(r.menuGaeste);
  const zubuchSub = (z: Zubuch, m: number): string => {
    const betrag = zeilenbetrag(z, wahl);
    if (z.art === "zaehler") {
      const je = z.gaesteCents ? z.gaesteCents[1]! : z.cents;
      if (z.ersetzt || z.gaesteCents) return je > 0 && je * m === betrag ? t.gaesteMal(m, money(je)) : t.gaeste(m);
      return `${m} × ${money(je)}`;
    }
    if (z.art === "alle") {
      const n = z.item.guest_count && z.item.guest_count > 0 ? z.item.guest_count : o.guest_count;
      const pp = z.item.unit_price_cents;
      return pp > 0 && pp * n === betrag ? t.gaesteMal(n, money(pp)) : t.fuerAlle(n);
    }
    return t.pauschal;
  };
  // Steuer wie im Entwurf: aufsteigend nach Satz in einer Zeile („davon 7 %: … · 19 %: …“).
  const ustTeile = (ust ?? [])
    .slice()
    .sort((a, b) => a.rateBp - b.rateBp)
    .map((u) => t.ustTeil(String(u.rateBp / 100).replace(".", ","), money(u.vatCents)));
  return (
    <aside className="ga-kasten" data-testid="ga-kasten" aria-label={t.kastenTitel}>
      <h3>{t.kastenTitel}</h3>
      {angenommen && (
        <p className="ga-angenommen" data-testid="ga-angenommen">
          <Icon name="check_circle" className="text-[20px]" />
          <span>{angenommenZeile ?? <><b>{t.angenommenBadge}.</b> {t.angenommenGebucht}</>}</span>
        </p>
      )}
      <ul className="ga-k-liste" aria-live="polite">
        {r.menuGaeste > 0 && (
          <li data-testid="ga-k-menu">
            <span className="ga-k-links">
              <span className="ga-k-name">{titel ?? t.menue}</span>
              <span className="ga-k-sub2" data-testid="ga-k-menu-gaeste">
                {alc ? t.gaeste(r.menuGaeste) : menuMal}
              </span>
              {wahlgaenge.map((w) => (
                <span key={w.id} className="ga-k-sub2" data-testid={`ga-k-wahlgang-${w.id}`}>
                  {w.text}
                </span>
              ))}
              {einzel.map((x) => (
                <span key={x} className="ga-k-sub2">
                  {x}
                </span>
              ))}
            </span>
            <span className="ga-k-betrag" data-testid="ga-k-menu-betrag">
              {alc ? t.nachKarte : money(r.menuCents)}
            </span>
          </li>
        )}
        {zub.map((z) => {
          const m = menge(wahl, z);
          return (
            <li key={z.id} data-testid={`ga-k-zubuchung-${z.id}`}>
              <span className="ga-k-links">
                <span className="ga-k-name">{name(z.item)}</span>
                <span className="ga-k-sub2">{zubuchSub(z, m)}</span>
              </span>
              <span className="ga-k-betrag">{money(zeilenbetrag(z, wahl))}</span>
            </li>
          );
        })}
        {mu && r.aufstockungCents > 0 && (
          <li data-testid="ga-k-aufstockung">
            <span className="ga-k-links">
              <span className="ga-k-name">{t.aufstockung(name(mu.item))}</span>
            </span>
            <span className="ga-k-betrag">{money(r.aufstockungCents)}</span>
          </li>
        )}
      </ul>
      <div className="ga-k-summe" data-testid="ga-k-gesamt">
        <span>{t.gesamt}</span>
        <span data-testid="ga-k-gesamt-betrag">{alc && r.gewaehlt === 0 ? t.nachKarte : angenommen && serverGesamtCents != null && serverGesamtCents > 0 ? money(serverGesamtCents) : money(r.totalCents)}</span>
      </div>
      {ustTeile.length > 0 && (
        <p className="ga-k-steuer" data-testid="ga-k-ust">
          <span>{t.inklMwst}</span>
          <span>{t.davonUst(ustTeile)}</span>
        </p>
      )}
      {anzahlung && anzahlung.cents != null && anzahlung.cents > 0 && (
        <p className="ga-k-zeile" data-testid="ga-k-anzahlung">
          <span>
            {anzahlung.vollzahlung ? t.vollzahlung : t.anzahlung}
            {!anzahlung.vollzahlung && anzahlung.prozentBp != null && anzahlung.prozentBp > 0 ? ` ${t.anzahlungProzent(anzahlung.prozentBp)}` : ""}
          </span>
          <b>{money(anzahlung.cents)}</b>
        </p>
      )}
      {anzahlung?.spaeter && <p className="ga-k-klein">{t.anzahlungSpaeter}</p>}
      {angenommen ? (
        <p className="ga-k-hinweis" data-testid="ga-angenommen-hinweis">
          {t.angenommenHinweis}
        </p>
      ) : (
        <button type="button" className="ga-annehmen" aria-disabled={annehmenAktiv ? "false" : "true"} data-testid="ga-annehmen" onClick={onAnnehmen}>
          {annehmenText}
        </button>
      )}
      {angenommen ? null : vorschau ? (
        <p className="ga-k-hinweis" data-testid="ga-vorschau-hinweis">
          {t.hinweisVorschau}
        </p>
      ) : annehmenSperre ? (
        <p className="ga-k-hinweis" data-testid="ga-annehmen-hinweis">
          {annehmenSperre}
        </p>
      ) : null}
      {!vorschau && !angenommen && <p className="ga-k-hinweis" data-testid="ga-annahme-verbindlich">{t.annahmeVerbindlich}</p>}
    </aside>
  );
}

/* ───────────── Hauptbaustein ───────────── */

export interface AuswahlProps {
  offer: PublicOffer;
  lang: "de" | "en";
  /** Die Varianten in der Reihenfolge, in der sie gezeigt werden. */
  optionen: AngebotOption[];
  selected: string | null;
  onSelect: (id: string) => void;
  wahl: Wahl;
  onWahl: (aenderung: Wahl) => void;
  mengen: Mengen;
  onMenge: (gruppeId: string, itemId: string, n: number) => void;
  /** Nichts ist veränderbar (Vorschau des Betriebs, abgelaufen, Kostenübernahme-Weg). */
  nurAnsicht: boolean;
  vorschau: boolean;
  markenfarbe: string | null | undefined;
  /** Der Server hat den Betrag/die Anzahlung zur aktuellen Auswahl geliefert. */
  anzahlung: AnzahlungAnzeige | null;
  onAnnehmen: () => void;
  /** Soll „Angebot annehmen“ überhaupt zählen (Phase bereit, nicht abgelaufen)? */
  annehmenMoeglich: boolean;
  annehmenText?: string;
  /** Mobile: das Blatt „Meine Auswahl“ ist offen. */
  blattOffen: boolean;
  onBlatt: (offen: boolean) => void;
  speicherFehler: boolean;
  foto: (idx: number) => { src: string; stil?: CSSProperties };
  sektionLabel: (section: string) => string;
  positionsName: (it: PublicOfferItem, o: AngebotOption) => string;
  zusatz: (optionId: string) => PublicOfferItem[];
  aLaCarte: (o: AngebotOption) => boolean;
  proPersonCents: (o: AngebotOption) => number | null;
  /** Hinweis an einer optionalen Position ohne buchbaren Preis („nicht im Gesamtpreis enthalten“) – Text der Seite. */
  /** Zusatzzeile unter dem Kopf einer Karte (z. B. Zahlungsstand in der Vorschau des Betriebs). */
  kartenZusatz?: (o: AngebotOption, gewaehlt: boolean) => ReactNode;
  optionalTag: string;
  /** Hinweis „zzgl. zur Pauschale“ – Text der Seite. */
  surchargeTag: string;
  /** Erhöht sich bei jedem Klick auf „annehmen“ ohne vollständige Zuordnung → Gruppen zeigen „Bitte wählen“. */
  fehltZeigen: boolean;
  onFehltZeigen: () => void;
  /** Punkt 61: Wahl je Gruppe ohne Gäste-Verzeichnis (`{ gruppe: position }`), von der Seite sofort gespeichert. */
  menuWahl?: Record<string, string>;
  onMenuWahl?: (gruppeId: string, itemId: string) => void;
  /**
   * Nach der Annahme (30.10.2026, Mockup „Zubuchen“ Zustand „angenommen“): dieselbe Karte nur lesend. Wahl, Mengen und
   * Zubuchungen kommen aus dem Gespeicherten (`wahl`/`mengen`); statt „Angebot annehmen“ steht „Angebot angenommen“.
   * Eine noch offene Einzelwahl („pro Tafel“) bleibt bedienbar, wenn `onMenuWahl` gesetzt ist.
   */
  angenommen?: boolean;
  /** Nach der Annahme: Zeile im Kasten, wenn die Buchung noch nicht verbindlich ist (Statuslogik der Seite); sonst der Standardsatz. */
  angenommenZeile?: string;
  /** Nach der Annahme: false = noch nicht verbindlich, Zubuchungen heißen dann „Angenommen“ statt „Verbindlich gebucht“. */
  verbindlich?: boolean;
  /** Nach der Annahme, Vorgang erstattet: keine Aussage zur Verbindlichkeit („Gewählt“ statt „Verbindlich gebucht“). */
  neutral?: boolean;
  /** Nach der Annahme: Summe vom Server (Cent), gilt statt der Rechnung aus den Positionen. */
  serverGesamtCents?: number | null;
  /** Nach der Annahme: die Menüwahl steht gerade in einer eigenen, bedienbaren Ansicht (Wahl nach der Buchung) - hier nicht doppelt zeigen. */
  ohneMenuewahl?: boolean;
}

export function AngebotsAuswahl(p: AuswahlProps) {
  const { offer, lang, optionen, selected } = p;
  const t = AT[lang];
  const { akzent, auf } = akzentFarben(p.markenfarbe ?? offer.markenfarbe);
  const wurzel = useRef<HTMLDivElement | null>(null);
  const waehlbar = optionen.length > 1;
  const gewaehlt = optionen.find((o) => o.id === selected) ?? null;

  const itemName = (it: PublicOfferItem) => it.translations?.[lang]?.name ?? it.name;
  const itemText = (it: PublicOfferItem) => {
    const x = it.translations?.[lang]?.description ?? it.description;
    return x && x.trim() ? x : null;
  };

  const r = gewaehlt ? rechne(gewaehlt, p.wahl, p.mengen) : null;

  // Wahlgänge der gewählten Variante: alle müssen genau aufgehen.
  const stand = gewaehlt && r ? wahlStand(gewaehlt, p.mengen, r.menuGaeste) : [];
  const offenerGang = stand.find((x) => !x.stand.ok) ?? null;
  // sql/772: Menüwahl im Paket - eine unvollständige Verteilung hält die Annahme an, außer die Wahl darf auf später.
  const menuWahlen = gewaehlt ? menuewahlenAus(gewaehlt) : [];
  const offenesMenue = menuWahlen.map((m) => ({ m, lage: menuewahlLage(m, p.mengen) })).find((x) => menuewahlSperrt(x.m, x.lage)) ?? null;
  const ueberbucht = r?.ueberbucht === true;
  // Zusatz-Schutz (28.10.2026): Variante ohne festen Preis und noch nichts gewählt (Summe 0 €) - nicht annehmbar.
  const nichtsGewaehlt = gewaehlt != null && r != null && r.totalCents === 0 && nurZubuchungen(gewaehlt, p.aLaCarte(gewaehlt));
  const annehmenAktiv = p.annehmenMoeglich && !p.vorschau && gewaehlt != null && !offenerGang && !offenesMenue && !ueberbucht && !nichtsGewaehlt;
  const sperre = !gewaehlt
    ? null
    : nichtsGewaehlt
      ? t.hinweisNichtsGewaehlt
      : ueberbucht
      ? t.hinweisUeberbucht
      : offenerGang
        ? offenerGang.stand.zuViel > 0
          ? t.hinweisZuViel(offenerGang.gang.gang ?? t.wahlgangName, offenerGang.stand.zuViel)
          : t.hinweisWahl(offenerGang.gang.gang ?? t.wahlgangName, offenerGang.stand.offen)
        : offenesMenue
          ? offenesMenue.lage.zuViel > 0
            ? t.hinweisMenueZuViel(offenesMenue.lage.zuViel)
            : t.hinweisMenue(offenesMenue.lage.offen)
          : null;

  function klickAnnehmen() {
    if (!annehmenAktiv) {
      if (offenerGang || offenesMenue) {
        p.onFehltZeigen();
        const el = typeof document !== "undefined" ? document.querySelector(offenerGang ? `[data-wahlgang="${offenerGang.gang.gruppeId}"]` : `[data-menuewahl="${offenesMenue!.m.gruppeId}"]`) : null;
        if (el && typeof (el as HTMLElement).scrollIntoView === "function") (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    p.onAnnehmen();
  }

  // Ohne CSS-Subgrid gleicht ein kleines Skript die Kopfzeilen der Karten an (nur Rückfall).
  useLayoutEffect(() => {
    const w = wurzel.current;
    if (!w || typeof window === "undefined") return;
    const kann = typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("grid-template-rows", "subgrid");
    if (kann) return;
    const gleiche = () => {
      const karten = [...w.querySelectorAll<HTMLElement>(".ga-karte")];
      const teile = ["foto", "kopf", "preis", "gaeste", "knopf"];
      for (const teil of teile) for (const k of karten) k.querySelectorAll<HTMLElement>(`[data-ga-teil="${teil}"]`).forEach((e) => (e.style.minHeight = ""));
      const zeilen = new Map<number, HTMLElement[]>();
      for (const k of karten) zeilen.set(k.offsetTop, [...(zeilen.get(k.offsetTop) ?? []), k]);
      for (const gruppe of zeilen.values()) {
        for (const teil of teile) {
          const els = gruppe.map((k) => k.querySelector<HTMLElement>(`[data-ga-teil="${teil}"]`)).filter((e): e is HTMLElement => e != null);
          const hoch = Math.max(0, ...els.map((e) => e.offsetHeight));
          els.forEach((e) => (e.style.minHeight = `${hoch}px`));
        }
      }
    };
    gleiche();
    window.addEventListener("resize", gleiche);
    return () => window.removeEventListener("resize", gleiche);
  });

  // Esc schließt das Blatt auf dem Handy.
  useEffect(() => {
    if (!p.blattOffen) return;
    const zu = (e: KeyboardEvent) => e.key === "Escape" && p.onBlatt(false);
    window.addEventListener("keydown", zu);
    return () => window.removeEventListener("keydown", zu);
  }, [p.blattOffen, p]);

  const kasten = (
    <AuswahlKasten
      o={gewaehlt}
      wahl={p.wahl}
      r={r}
      mengen={p.mengen}
      name={itemName}
      anzahlung={p.anzahlung}
      annehmenSperre={sperre}
      onAnnehmen={klickAnnehmen}
      annehmenAktiv={annehmenAktiv}
      annehmenText={p.annehmenText ?? t.annehmen}
      angenommen={p.angenommen === true}
      angenommenZeile={p.angenommenZeile}
      serverGesamtCents={p.serverGesamtCents ?? null}
      vorschau={p.vorschau}
      alc={gewaehlt != null && p.aLaCarte(gewaehlt)}
      proPerson={gewaehlt ? p.proPersonCents(gewaehlt) : null}
      wahlgaenge={
        gewaehlt && r
          ? [
              ...wahlgangZeilen(gewaehlt, p.mengen, r.menuGaeste, (it) => p.positionsName(it, gewaehlt), itemText, t.wahlgangName),
              // sql/772: Menüwahl - nur eine vollständige Verteilung steht in „Ihre Auswahl“ (sie ist es, die der Server bucht).
              ...menuWahlen.flatMap((m) => {
                const z = menuewahlLage(m, p.mengen).ok ? menuewahlZeile(m, p.mengen, (it) => p.positionsName(it, gewaehlt), (a) => (a ? (t.menueArt[a] ?? a) : null)) : null;
                return z ? [{ id: m.gruppeId, text: z }] : [];
              }),
            ]
          : []
      }
      einzel={
        gewaehlt
          ? einzelwahlGruppen(gewaehlt, (it) => p.positionsName(it, gewaehlt), itemText).flatMap((g) => {
              const zl = g.zeilen.find((z) => z.it.id === p.menuWahl?.[g.gruppeId]);
              return zl ? [`${g.gang ?? t.wahlgangName}: ${zl.teile.titel ?? itemName(zl.it)}`] : [];
            })
          : []
      }
    />
  );

  const stil = { "--ga-akzent": akzent, "--ga-auf": auf } as CSSProperties;

  return (
    <TextKontext.Provider value={t}>
      <div className="ga" ref={wurzel} style={stil} data-testid="angebots-auswahl" data-angenommen={p.angenommen ? "true" : "false"}>
        <style>{STIL_CSS}</style>
        <div className="ga-layout">
          <div className="ga-karten" data-n={optionen.length} id="varianten-auswahl">
            {p.nurAnsicht && !p.vorschau && (
              <p className="ga-nur-ansicht" data-testid="ga-nur-ansicht" style={{ gridColumn: "1 / -1" }}>
                {t.wunschNurAnsicht}
              </p>
            )}
            {optionen.map((o, idx) => (
              <Karte key={o.id} p={p} o={o} idx={idx} waehlbar={waehlbar} istGewaehlt={o.id === selected} r={o.id === selected ? r : null} stand={o.id === selected ? stand : []} itemName={itemName} itemText={itemText} />
            ))}
          </div>
          {kasten}
        </div>
        {p.blattOffen && (
          <div className="ga-blatt-hinter" onClick={() => p.onBlatt(false)} data-testid="ga-blatt">
            <div className="ga-blatt" role="dialog" aria-modal="true" aria-label={t.kastenTitel} onClick={(e) => e.stopPropagation()}>
              <button type="button" className="ga-blatt-zu" onClick={() => p.onBlatt(false)} autoFocus>
                {t.schliessen}
              </button>
              {kasten}
            </div>
          </div>
        )}
        {p.speicherFehler && (
          <p role="alert" className="ga-k-hinweis">
            {t.speichertFehler}
          </p>
        )}
      </div>
    </TextKontext.Provider>
  );
}

/* ───────────── Gericht (eine Zeile der Speisekarte) ───────────── */

/**
 * Allergene und Diät-Kennzeichen einer Position (LMIV). Gemeinsam für die Speisekarte (`GerichtZeile`) und die Menü-Karten der
 * Menüwahl, damit beide dasselbe zeigen. Tri-State: nur gepflegte Angaben erscheinen; „nicht gepflegt“ bleibt leer und wird nie
 * als „keine Allergene“ angezeigt.
 */
function allergeneUndDiaet(it: PublicOfferItem, lang: "de" | "en"): { allergene: string; dietText: string } {
  const diet = DIETARY.filter((d) => it.dietary?.[d.key] === true);
  const allergene = Array.isArray(it.allergens) && it.allergens.length > 0 ? ` (${[...it.allergens].sort().join(", ")})` : "";
  const dietText = diet.length > 0 ? ` · ${diet.map((d) => (lang === "en" ? d.labelEn : d.label)).join(" · ")}` : "";
  return { allergene, dietText };
}

function GerichtZeile({ zl, oder, lang, optionalTag, surchargeTag }: { zl: KartenZeile<PublicOfferItem>; oder: boolean; lang: "de" | "en"; optionalTag: string; surchargeTag: string }) {
  const t = useT();
  const { allergene, dietText } = allergeneUndDiaet(zl.it, lang);
  const { titel, text } = zl.teile;
  return (
    <li className="ga-zeile" data-testid={`ga-gericht-${zl.it.id}`}>
      {oder && (
        <p className="ga-oder" aria-hidden="true">
          {t.oder}
        </p>
      )}
      {zl.art && <p className="ga-gericht-art">{zl.art}</p>}
      {titel ? (
        <p className="ga-gericht">
          {titel}
          {(allergene || dietText) && (
            <span className="ga-gericht-zusatz">
              {allergene}
              {dietText}
            </span>
          )}
        </p>
      ) : null}
      {(text || (!titel && (allergene || dietText))) && (
        <p className="ga-gericht-text">
          {text}
          {!titel && allergene}
          {!titel && dietText}
        </p>
      )}
      {zl.it.is_optional === true && <p className="ga-notiz">{optionalTag}</p>}
      {zl.it.surcharge === true && zl.it.is_optional !== true && <p className="ga-notiz">{surchargeTag}</p>}
    </li>
  );
}

/* ───────────── Karte ───────────── */

function Karte({
  p,
  o,
  idx,
  waehlbar,
  istGewaehlt,
  r,
  stand,
  itemName,
  itemText,
}: {
  p: AuswahlProps;
  o: AngebotOption;
  idx: number;
  waehlbar: boolean;
  istGewaehlt: boolean;
  r: Rechnung | null;
  stand: ReturnType<typeof wahlStand>;
  itemName: (it: PublicOfferItem) => string;
  itemText: (it: PublicOfferItem) => string | null;
}) {
  const { lang } = p;
  const t = AT[lang];
  const ms = o.menu_selection ?? {};
  const foto = p.foto(idx);
  const karte = o.items ?? [];
  const alc = p.aLaCarte(o);
  const empfohlen = o.is_recommended === true && p.optionen.length > 1;
  const pp = p.proPersonCents(o);
  // Zusatz-Schutz (28.10.2026): ohne festen Preis zeigt die Karte statt „0,00 € pro Person“ einen Hinweis.
  const nurWahl = nurZubuchungen(o, alc);
  const titel = o.title || ms.title || (alc ? t.nachKarte : money(o.amount_total_cents));
  const menuGaeste = r ? r.menuGaeste : o.guest_count;
  const menuCents = r ? r.menuCents : o.amount_total_cents;
  const zub = zubuchungenAus(o);
  const schalter = schalterAus(zub);
  const lesen = p.angenommen === true;
  const aktiv = istGewaehlt && !p.nurAnsicht && !lesen;
  const ersetzendeGewaehlt = zub.filter((z) => z.ersetzt && menge(p.wahl, z) > 0);
  const ersetztArt = ersetzendeGewaehlt.length === 1 ? ersetztArtAus(itemName(ersetzendeGewaehlt[0]!.item), o.title || ms.title) : null;
  const vegetarischZeile =
    r && r.ersetzt > 0
      ? ersetzendeGewaehlt.length === 1
        ? ersetztArt
          ? t.ersetztArt(r.ersetzt, ersetztArt.toLocaleLowerCase(lang === "de" ? "de-DE" : "en-GB"))
          : t.ersetztEine(r.ersetzt, itemName(ersetzendeGewaehlt[0]!.item))
        : t.ersetztMehrere(r.ersetzt)
      : "";
  const wahlgaenge = new Map(wahlgaengeAus(o).map((w) => [w.gruppeId, w]));
  const soll = r ? r.menuGaeste : o.guest_count;

  // Positionen nach Rubrik; buchbare Zubuchungen stehen unten unter „Auf Wunsch dazu“.
  const buchbarIds = new Set(zub.map((z) => z.id));
  // V-1262: die Gänge eines zubuchbaren Pakets stehen in dessen Zeile unter „Auf Wunsch dazu“, nicht im Hauptmenü.
  const bestandteile = paketBestandteile(karte, buchbarIds);
  // sql/772: die Menüs einer Menüwahl (Kopfzeilen und Gänge) stehen im Auswahl-Block, nicht in der Speisekarte der Karte.
  const menuePositionen = menuewahlPositionen(o);
  type Zeile = KartenZeile<PublicOfferItem>;
  const proRubrik = new Map<string, PublicOfferItem[]>();
  for (const it of karte) {
    if (it.section === "adjustment") continue;
    // Nach der Annahme zeigt die Karte nur Menü und Getränke; andere Bereiche (Equipment, Personal) erscheinen als gebuchte Zubuchung.
    if (lesen && it.section !== "menu" && it.section !== "drinks") continue;
    if (buchbarIds.has(it.id) || bestandteile.verschoben.has(it.id) || menuePositionen.has(it.id)) continue;
    const label = p.sektionLabel(it.section);
    proRubrik.set(label, [...(proRubrik.get(label) ?? []), it]);
  }
  // V-1262: die Kopfzeile eines Pakets heißt wie die Karte - ihr Name steht schon als Titel oben. Ihre Beschreibung
  // (Review #1851: paketZeilenBauen kopiert pkg.description dorthin) ist die Einordnung des Menüs und steht
  // direkt UNTER dem Titel (Gestaltung 28.10.2026), nicht als Absatz zwischen Knopf und erstem Gang. Trägt die
  // Kopfzeile Allergene oder einen Zuschlag, bleibt sie als Zeile der Karte stehen.
  const nurTitel = (it: PublicOfferItem): boolean => it.is_optional !== true && wiederholtTitel(itemName(it), titel);
  const einordnung: string[] = [];
  const rubriken = new Map<string, Zeile[]>(
    [...proRubrik.entries()]
      .map(([label, its]): [string, Zeile[]] => [
        label,
        speisekarteZeilen(its, (it) => p.positionsName(it, o), itemText).filter((zl) => {
          if (!nurTitel(zl.it)) return true;
          const text = itemText(zl.it);
          const bleibt = (Array.isArray(zl.it.allergens) && zl.it.allergens.length > 0) || zl.it.surcharge === true;
          if (text && !einordnung.includes(text)) einordnung.push(text);
          return bleibt;
        }).map((zl) => (nurTitel(zl.it) ? { ...zl, courseLabel: zl.it.course_label?.trim() || null, art: null, teile: { gang: null, titel: null, text: null } } : zl)),
      ])
      .filter(([, zeilen]) => zeilen.length > 0),
  );
  // Kurzzeile über dem Titel (Entwurf: „4 Gänge · Aperitif inklusive“) - nur, wenn sie aus der Speisekarte ableitbar ist:
  // genau eine Rubrik, jede Zeile hat einen Gang, mindestens zwei Gänge. Ein gepflegter Untertitel hat Vorrang.
  const kurzzeile = (() => {
    if (ms.description || rubriken.size !== 1) return null;
    const gaenge = gaengeAus([...rubriken.values()][0]!);
    if (gaenge.length < 2 || gaenge.some((g) => !g.label)) return null;
    const aperitif = gaenge.some((g) => /^(aperitif|aperitivo)$/i.test(g.label ?? ""));
    return [t.gaengeKurz(gaenge.length), aperitif ? t.aperitifInkl : null].filter(Boolean).join(" · ");
  })();
  // „Getränke: Nicht enthalten …“ - nur, wenn die Karte selbst keine Getränke führt und eine Getränke-Zubuchung für alle Gäste
  // existiert: Rubrik Getränke, oder (Review #1861) 19 % USt pro Person UND ein Getränke-Wort im Namen - 19 % allein
  // träfe auch Personal oder Technik pro Person.
  const getraenkeWort = /getr[äa]nk|drink|bevand|boisson|wein|wine|vino|vin\b|pauschale.*getr/i;
  const getraenkeZubuchbar =
    !rubriken.has(p.sektionLabel("drinks")) &&
    zub.some((z) => (z.art === "alle" || (z.gaesteCents != null && !z.ersetzt)) && (z.item.section === "drinks" || (z.vatBp === 1900 && getraenkeWort.test(z.item.name ?? ""))));
  const zusatz = p.zusatz(o.id);
  const kartenZusatz = p.kartenZusatz?.(o, istGewaehlt) ?? null;

  return (
    <article className="ga-karte" data-testid={`ga-karte-${o.id}`} data-gewaehlt={istGewaehlt ? "true" : "false"} aria-label={titel}>
      <div className="ga-foto" data-ga-teil="foto">
        <img src={foto.src} alt="" style={foto.stil} />
        {istGewaehlt && waehlbar && (
          <span className="ga-marke">
            <Icon name="check" className="text-[15px]" />
            {t.ihreWahl}
          </span>
        )}
        {empfohlen && (
          <span className="ga-empfehlung" data-testid="varianten-empfehlung">
            <Icon name="star" className="text-[15px]" />
            {lang === "en" ? "Our recommendation" : "Unsere Empfehlung"}
          </span>
        )}
      </div>
      <div className="ga-teil ga-kopf" data-ga-teil="kopf">
        {ms.description ? <p className="ga-unter">{ms.description}</p> : kurzzeile ? <p className="ga-unter" data-testid={`ga-kurzzeile-${o.id}`}>{kurzzeile}</p> : null}
        <h2 className="ga-titel">{titel}</h2>
        {einordnung.map((x) => (
          <p key={x} className="ga-einordnung" data-testid={`ga-einordnung-${o.id}`}>
            {x}
          </p>
        ))}
      </div>
      <p className="ga-teil ga-preis" data-ga-teil="preis" data-testid={`ga-preis-${o.id}`}>
        {alc ? (
          <span data-testid="v2-preis-a-la-carte">{A_LA_CARTE_GAST[lang].preisZeile}</span>
        ) : nurWahl ? (
          <small data-testid={`ga-nur-wahl-${o.id}`}>{t.nurWahlPreis}</small>
        ) : pp != null ? (
          <>
            <span>{money(pp)}</span>
            <small>{t.proPerson}</small>
          </>
        ) : (
          money(o.amount_total_cents)
        )}
      </p>
      <div className="ga-teil ga-gaeste" data-ga-teil="gaeste">
        <p data-testid={`ga-gaeste-${o.id}`}>
          {alc || o.guest_count <= 0 ? (
            ""
          ) : nurWahl && menuCents === 0 ? (
            t.gaeste(menuGaeste)
          ) : (
            <>
              {t.gaeste(menuGaeste)} · <b>{money(menuCents)}</b>
            </>
          )}
        </p>
        <p data-testid={`ga-ersetzt-${o.id}`}>{vegetarischZeile}</p>
      </div>
      <div className="ga-knopf-zelle" data-ga-teil="knopf">
        {waehlbar && !lesen && (
          <button type="button" className="ga-knopf" aria-pressed={istGewaehlt} data-testid={`ga-waehlen-${o.id}`} onClick={() => p.onSelect(o.id)}>
            {istGewaehlt && <Icon name="check" className="ga-haken text-[18px]" />}
            {istGewaehlt ? t.gewaehlt : t.waehlen}
          </button>
        )}
      </div>
      <div className="ga-koerper" data-ga-teil="koerper">
        {kartenZusatz != null && kartenZusatz !== false ? <div className="ga-gang">{kartenZusatz}</div> : null}
        {zusatz.length > 0 && (
          <ul className="ga-gang" data-testid="varianten-zusatz" style={{ margin: 0, listStyle: "none", fontSize: 14, fontWeight: 600 }}>
            {zusatz.slice(0, 4).map((it, zi) => (
              <li key={zi}>+ {it.translations?.[lang]?.name ?? it.name}</li>
            ))}
            {zusatz.length > 4 && <li>{lang === "en" ? `+ ${zusatz.length - 4} more` : `+ ${zusatz.length - 4} weitere`}</li>}
          </ul>
        )}
        {!p.ohneMenuewahl && <MenuewahlBloecke p={p} o={o} aktiv={aktiv} zeigeFehlt={istGewaehlt && p.fehltZeigen} lesen={lesen} name={(it) => p.positionsName(it, o)} beschreibung={itemText} lang={lang} />}
        {rubriken.size === 0 && menuePositionen.size === 0 &&
          (ms.items ?? []).map((item, i) => (
            <div key={i} className="ga-gang">
              <p className="ga-gericht">{item.name}</p>
            </div>
          ))}
        {[...rubriken.entries()].map(([sektion, zeilen]) => (
          <section key={sektion} className="ga-rubrik">
            {rubriken.size > 1 && <h4 className="ga-sektion-titel">{sektion}</h4>}
            {gaengeAus(zeilen).map((gang, gi) => (
              <div key={`${gang.label ?? "ohne"}-${gi}`} className="ga-gang">
                {gang.label && <h5 className="ga-gang-titel">{gang.label}</h5>}
                {alternativenAus(gang.lines).map((block, bi) => {
                  const gruppeId = block.alternativen ? (block.lines[0]?.choiceGroupId ?? null) : null;
                  const wg = gruppeId ? wahlgaenge.get(gruppeId) : undefined;
                  const abstand = bi > 0 ? { marginTop: 12 } : undefined;
                  if (wg) {
                    return (
                      <div key={bi} style={abstand}>
                        <WahlgangBlock
                          wg={wg}
                          mengen={p.mengen}
                          soll={wahlSoll(wg, soll, o.guest_count)}
                          aktiv={aktiv}
                          zeigeFehlt={istGewaehlt && p.fehltZeigen}
                          name={(it) => p.positionsName(it, o)}
                          beschreibung={itemText}
                          aufpreis={(id) => alternativeAufpreis(o, id)}
                          onMenge={p.onMenge}
                          zeilen={block.lines}
                          lesen={lesen}
                        />
                      </div>
                    );
                  }
                  if (gruppeId && p.onMenuWahl) {
                    return (
                      <div key={bi} style={abstand}>
                        <EinzelwahlBlock gruppeId={gruppeId} gang={gang.label} zeilen={block.lines} gewaehlt={p.menuWahl?.[gruppeId]} aktiv={aktiv || (lesen && !p.vorschau && !p.nurAnsicht && p.menuWahl?.[gruppeId] == null)} onWahl={p.onMenuWahl} lesen={lesen} />
                      </div>
                    );
                  }
                  return (
                    <ul key={bi} className="ga-liste" style={abstand} data-oder={block.alternativen ? "true" : "false"}>
                      {block.lines.map((zl, li) => (
                        <GerichtZeile key={zl.it.id} zl={zl} oder={block.alternativen && li > 0} lang={lang} optionalTag={p.optionalTag} surchargeTag={p.surchargeTag} />
                      ))}
                    </ul>
                  );
                })}
              </div>
            ))}
          </section>
        ))}
        {getraenkeZubuchbar && (
          <div className="ga-gang" data-testid={`ga-getraenke-hinweis-${o.id}`}>
            <h5 className="ga-gang-titel">{t.getraenkeTitel}</h5>
            <p className="ga-gericht-text" style={{ marginTop: 0 }}>
              {t.getraenkeZubuchbar}
            </p>
          </div>
        )}
        {schalter.length > 0 && (
          <div className="ga-wunsch" data-aktiv={aktiv ? "true" : "false"} data-testid={`ga-wunsch-${o.id}`}>
            <h4 className="ga-wunsch-titel">{lesen ? t.zubuchungenTitel : t.wunschTitel}</h4>
            {!lesen && <p className="ga-wunsch-hinweis">{t.wunschHinweis}</p>}
            <ul className="ga-zb-liste">
              {schalter.map((s) => (
                <ZubuchZeile
                  key={s.key}
                  s={s}
                  o={o}
                  wahl={p.wahl}
                  r={r ?? rechne(o, {})}
                  aktiv={aktiv}
                  nurAnsicht={p.nurAnsicht}
                  name={itemName}
                  beschreibung={itemText}
                  onWahl={p.onWahl}
                  lesen={lesen}
                  verbindlich={p.verbindlich !== false}
                  neutral={p.neutral === true}
                  gaenge={s.members.flatMap((m) => bestandteile.nachZubuchung.get(m.id) ?? [])}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  );
}
